/**
 * Per-property "Reminders" tab.
 *
 * Two-pane flow:
 *   1. List mode (default) — table of every tenant on this property with
 *      filter chips, "Sort by days overdue" / "All properties" pills, and
 *      a stage-specific Draft button on each row.
 *   2. Detail mode — opens when a row is clicked. Shows that single tenant
 *      with a back button: alert banner, 4-pip stage tracker, activity
 *      timeline, and the Outstanding right-rail panel with Draft +
 *      Mark-as-paid actions.
 *
 * The Email-draft modal supports a working DE/EN toggle: switching the
 * flag re-renders the preview from the matching template, and the chosen
 * language is what the edge function dispatches.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "../../../../lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertCircle,
  ArrowDownUp,
  ArrowLeft,
  Bed,
  Calendar,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  Download,
  Euro,
  FileCheck,
  FileSpreadsheet,
  FileText,
  Loader2,
  Mail,
  MapPin,
  Phone,
  Search,
  Send,
  TriangleAlert,
  Users as UsersIcon,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import {
  computeCurrentStage,
  computeDaysOverdue,
  currentPeriodDueDate,
  isPaidCurrentPeriod,
  paymentStatusForStage,
  reminderInternalLabel,
  reminderScheduleLines,
  reminderStatusLabel,
  renderReminderEmail,
  resolveDunningFee,
  type ReminderLang,
  type ReminderStage,
  type ReminderTone,
} from "@/lib/rent-reminder-templates";
import { recordManualRentPaidInLedger } from "@/lib/manual-rent-paid";
import { edgeFunctionErrorMessage } from "@/lib/edge-function-error";
import { isRentCollectionEligible } from "@/lib/rent-collection";
import { useTenantJourney } from "@/hooks/v2/useTenantJourneys";
import { TenantJourneyStageBadge } from "@/components/admin/v2/tenant/TenantJourneyStageBadge";
import { effectiveRentForMonthEUR } from "@/lib/rent-discount";
import { fetchRentLedgerForBookings } from "@/lib/rent-ledger-fetch";
import {
  nextStageInChain,
  currentPeriodLedger,
  currentRowsForFeePlacement,
  daysSince,
  keepInvoicedCurrentRows,
  placeBookingFees,
  selectOpenPastMonthRent,
} from "@/lib/rent-past-months";
import { PROPERTY_REMINDER_STATS_KEY } from "@/hooks/v2/usePropertyReminderStats";
import { PROPERTY_RENT_PAID_STATS_KEY } from "@/hooks/v2/usePropertyRentPaidStats";
import { invalidateOwnerCaches } from "@/hooks/v2/owner-detail-keys";
import { useLanguage } from "@/contexts/LanguageContext";
import { formatDateNumeric } from "@/lib/date-format";
import { invalidateOccupancyViews } from "@/hooks/v2/useProperties";

// ───────────────────────────────────────────────────────────────────────────
// Types & helpers
// ───────────────────────────────────────────────────────────────────────────

type PaymentStatus =
  "paid" | "pending" | "overdue_1" | "overdue_2" | "overdue_3" | "inkasso";

type FilterKey =
  | "all"
  | "all_overdue"
  | "stage_1"
  | "stage_2_3"
  | "inkasso"
  | "paid_this_month"
  | "past_months";

type SortKey = "days_overdue" | "amount" | "name";

interface TenantRow {
  booking_id: number;
  user_id: string | null;
  room_id: number | null;
  /**
   * What this row is CHASING — the still-open rent, in EUR (COL-417).
   *
   * Every total, sort, export, mark-paid amount and drafted reminder in this
   * view reads this one field, which is why the partial subtraction happens
   * here rather than at each of those call sites: a reminder that demands the
   * full month from a tenant who already part-paid is the single worst thing
   * this screen can do.
   */
  monthly_rent: number;
  /** What the month BILLS, before any receipt. Display only. */
  rent_billed_eur: number;
  /** Cents already received against the month, in EUR. Zero when none. */
  rent_received_eur: number;
  payment_status: PaymentStatus;
  rent_due_date: number | null;
  reminder_stage: 0 | 1 | 2 | 3 | 4;
  reminder_sent_at: string | null;
  accumulated_fees_eur: number;
  inkasso_referred_at: string | null;
  last_payment_received_at: string | null;
  tenant_name: string | null;
  tenant_email: string | null;
  room_title: string | null;
  property_title: string | null;
  property_city: string | null;
  days_overdue: number;
  current_stage: 0 | ReminderStage;
  is_confirmed_active: boolean;
  /** Whether automated reminders run for this unit (apartment override, else
   *  property default). false = suppressed from overdue detection/sends. */
  reminders_enabled: boolean;
  /** Per-property Mahngebühr in EUR (COL-308), resolved via resolveDunningFee
   *  so previews match the fee the edge function renders into sent emails. */
  fee_per_stage_eur: number;
  /** Stable list key: the booking id for the current-period row,
   *  `booking:YYYY-MM` for a past-month row (COL-435). */
  row_key: string;
  /** Which billing period this row is about. A booking has one "current" row
   *  and one "past" row per earlier month still open in the ledger. */
  period_kind: "current" | "past";
  /** Past rows only: ISO due date of the open ledger row (the month it pays for). */
  period_due_date: string | null;
  /** Concrete due date for the current invoice (move-in date for first rent). */
  invoice_due_date: string | null;
  /** Past rows only: the `payments` row backing this item. */
  ledger_payment_id: string | null;
  /** The booking's accumulated Mahngebühren (booking-level). Drafts render
   *  from this so a past-month preview matches what the edge function sends;
   *  `accumulated_fees_eur` is what THIS row displays (fees appear on one row). */
  booking_fees_eur: number;
}

interface ActivityRow {
  id: string;
  event_type:
    "payment_received" | "reminder_sent" | "marked_paid" | "inkasso_referred";
  stage: number | null;
  amount_eur: number | null;
  email_lang: "de" | "en" | null;
  created_at: string;
  created_by: string | null;
}

function formatEur(eur: number): string {
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(eur || 0));
}

function formatDateShort(d: Date, language?: string): string {
  return d.toLocaleDateString(language === "de" ? "de-DE" : "en-GB", {
    day: "numeric",
    month: "short",
  });
}

function formatDateLong(d: Date): string {
  return d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function monthLabelDe(d: Date): string {
  return d.toLocaleDateString("de-DE", { month: "long", year: "numeric" });
}

function monthLabelEn(d: Date): string {
  return d.toLocaleDateString("en-GB", { month: "long", year: "numeric" });
}

function formatDateDe(d: Date): string {
  return d.toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function formatDateEn(d: Date): string {
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

/** Compact fee label for inline UI copy, e.g. "€5" or "€7.50". */
function feeLabel(eur: number): string {
  return `€${Number.isInteger(eur) ? eur : eur.toFixed(2)}`;
}

/** Due date of the period a row is about: the ledger due date for a past
 *  month, otherwise the booking's current billing period. */
function rowPeriodDue(
  row: Pick<
    TenantRow,
    "period_due_date" | "invoice_due_date" | "rent_due_date"
  >,
  today: Date = new Date()
): Date {
  if (row.period_due_date) return new Date(row.period_due_date);
  if (row.invoice_due_date) return new Date(row.invoice_due_date);
  return row.rent_due_date
    ? currentPeriodDueDate(row.rent_due_date, today)
    : today;
}

// Plain-language pill styling per tone; internal stage/Inkasso terms are kept
// on hover only (see reminderInternalLabel).
const TONE_STYLES: Record<
  ReminderTone,
  { bg: string; text: string; border: string; dot: string }
> = {
  paid: {
    bg: "bg-emerald-50",
    text: "text-emerald-700",
    border: "border-emerald-200",
    dot: "bg-emerald-500",
  },
  late: {
    bg: "bg-amber-50",
    text: "text-amber-800",
    border: "border-amber-200",
    dot: "bg-amber-500",
  },
  final: {
    bg: "bg-rose-50",
    text: "text-rose-800",
    border: "border-rose-300",
    dot: "bg-rose-600",
  },
  pending: {
    bg: "bg-slate-50",
    text: "text-slate-700",
    border: "border-slate-200",
    dot: "bg-slate-400",
  },
  off: {
    bg: "bg-slate-50",
    text: "text-slate-500",
    border: "border-slate-200",
    dot: "bg-slate-300",
  },
};

function StatusPill({ row }: { row: TenantRow }) {
  const { label, tone } = reminderStatusLabel({
    reminders_enabled: row.reminders_enabled,
    paid: row.payment_status === "paid",
    days_overdue: row.days_overdue,
    stage: row.current_stage,
  });
  const s = TONE_STYLES[tone];
  const title =
    row.reminders_enabled && tone !== "paid" && row.current_stage > 0
      ? reminderInternalLabel(row.current_stage)
      : undefined;
  return (
    <Badge className={cn("gap-1.5", s.bg, s.text, s.border)} title={title}>
      <span className={cn("inline-block h-1.5 w-1.5 rounded-full", s.dot)} />
      {label}
    </Badge>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Tenant profile dialog
// ───────────────────────────────────────────────────────────────────────────

function formatDateProfile(s: string | null | undefined): string {
  if (!s) return "—";
  return new Date(s).toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function TenantProfileDialog({
  bookingId,
  onClose,
}: {
  bookingId: number | null;
  onClose: () => void;
}) {
  const { t, language } = useLanguage();
  const open = bookingId != null;

  const { data, isLoading } = useQuery({
    queryKey: ["reminder-tenant-profile", bookingId],
    enabled: bookingId != null,
    queryFn: async () => {
      const { data: bookingData, error: bErr } = await supabase
        .from("bookings")
        .select(
          // Includes every column resolveTenantJourney's gates read directly
          // off the booking row (key pickup/return, move-in/move-out
          // evidence, archived) — the earlier select only carried enough for
          // this dialog's own fields, and a resolver gate reading `undefined`
          // here silently capped every tenant's derived stage at "Key pickup
          // pending", one screen away from what the tenant profile showed. The
          // two protocol-reopen stamps belong to that list too: useTenantJourney
          // retires a submitted protocol a later reopen has superseded, and
          // without them a reopened move-out stayed at "Key return pending".
          "id, user_id, phone, move_in_date, move_out_date, actual_move_in_date, actual_move_out_date, start_date, end_date, contract_signed_at, contract_status, contract_pdf_url, contract_url, signed_contract_url, docusign_envelope_id, key_picked_up_at, key_returned_at, move_in_confirmed_at, move_out_notice_at, move_in_protocol_reopened_at, move_out_protocol_reopened_at, admin_archived_at, admin_archive_reason, monthly_rent, deposit_amount, room_id, status"
        )
        .eq("id", bookingId)
        .maybeSingle();
      if (bErr) throw new Error(bErr.message);
      const booking = bookingData as any;
      if (!booking) return null;

      const [profileRes, roomRes] = await Promise.all([
        booking.user_id
          ? supabase
              .from("profiles")
              .select("id, name, email, phone")
              .eq("id", booking.user_id)
              .maybeSingle()
          : Promise.resolve({ data: null, error: null }),
        booking.room_id
          ? supabase
              .from("rooms")
              .select("id, title, property_id, properties(id, title)")
              .eq("id", booking.room_id)
              .maybeSingle()
          : Promise.resolve({ data: null, error: null }),
      ]);

      const profile = (profileRes.data ?? null) as {
        id: string;
        name: string | null;
        email: string | null;
        phone: string | null;
      } | null;

      const room = (roomRes.data ?? null) as {
        id: number;
        title: string | null;
        property_id: number | null;
        properties: { id: number; title: string | null } | null;
      } | null;

      return { booking, profile, room };
    },
  });

  const booking = data?.booking as any;
  const profile = data?.profile;
  const room = data?.room;

  const name = profile?.name ?? null;
  const email = profile?.email ?? null;
  const phone = (profile?.phone ?? booking?.phone ?? null) as string | null;

  const initials =
    (name || email || "?")
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((p: string) => p[0]?.toUpperCase())
      .join("") || "?";

  const moveIn =
    (booking?.actual_move_in_date as string | null) ??
    (booking?.move_in_date as string | null) ??
    (booking?.start_date as string | null);
  const moveOut =
    (booking?.actual_move_out_date as string | null) ??
    (booking?.move_out_date as string | null) ??
    (booking?.end_date as string | null);

  // The derived stage, from the same evidence the tenant profile reads.
  const { journey } = useTenantJourney(booking ?? null);

  const contractStatus = (booking?.contract_status as string | null) ?? null;
  const contractSigned = (booking?.contract_signed_at as string | null) ?? null;
  const contractPdfUrl = (booking?.contract_pdf_url as string | null) ?? null;

  const roomTitle = room?.title ?? null;
  const propertyTitle = room?.properties?.title ?? null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[500px] rounded-3xl p-0 overflow-hidden gap-0 border-0 shadow-2xl [&>button]:text-white/70 [&>button]:hover:text-white [&>button]:top-4 [&>button]:right-4">
        <DialogHeader className="sr-only">
          <DialogTitle>
            {t("admin.remindersTab.tenantProfile", "Tenant profile")}
          </DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="p-8 space-y-4">
            <div className="flex items-center gap-4">
              <div className="h-20 w-20 rounded-full bg-muted animate-pulse shrink-0" />
              <div className="space-y-2 flex-1">
                <div className="h-5 w-36 rounded-lg bg-muted animate-pulse" />
                <div className="h-4 w-48 rounded-lg bg-muted animate-pulse" />
                <div className="h-4 w-32 rounded-lg bg-muted animate-pulse" />
              </div>
            </div>
            <div className="h-px bg-border" />
            <div className="grid grid-cols-2 gap-3">
              {[1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  className="h-16 rounded-xl bg-muted animate-pulse"
                />
              ))}
            </div>
          </div>
        ) : !data ? (
          <div className="p-8 text-sm text-muted-foreground">
            {t(
              "admin.remindersTab.couldNotLoadTenantData",
              "Could not load tenant data."
            )}
          </div>
        ) : (
          <>
            {/* ── Hero header ──────────────────────────────────────────── */}
            <div className="relative bg-gradient-to-br from-emerald-600 via-emerald-700 to-teal-800 px-6 pt-8 pb-6">
              <div className="flex items-center gap-4">
                {/* Avatar */}
                <div className="h-20 w-20 shrink-0 rounded-2xl bg-white/20 backdrop-blur-sm text-white flex items-center justify-center text-2xl font-bold border border-white/30 shadow-lg">
                  {initials}
                </div>

                <div className="min-w-0 text-white">
                  <h2 className="text-xl font-bold tracking-tight truncate">
                    {name ??
                      `${t("owner.reminders.bookingHashPrefix", "Booking")} #${booking?.id ?? "—"}`}
                  </h2>
                  <div className="mt-1.5 space-y-1">
                    {email && (
                      <div className="flex items-center gap-1.5 text-sm text-white/80">
                        <Mail className="h-3.5 w-3.5 shrink-0" />
                        <span className="truncate">{email}</span>
                      </div>
                    )}
                    {phone && (
                      <div className="flex items-center gap-1.5 text-sm text-white/80">
                        <Phone className="h-3.5 w-3.5 shrink-0" />
                        <span>{phone}</span>
                      </div>
                    )}
                    {!email && !phone && (
                      <div className="text-sm text-white/60">
                        {t(
                          "admin.remindersTab.noContactInfo",
                          "No contact info"
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Derived journey stage — the same pill the tenant profile,
                  the Tenants pipeline and the room view show. This used to
                  print `bookings.status` ("Confirmed"), a word that appears on
                  no other surface and that can claim a stage the evidence
                  does not support. */}
              {journey && (
                <div className="mt-4">
                  <TenantJourneyStageBadge
                    stageId={journey.stageId}
                    terminalStatus={journey.terminalStatus}
                    archived={!!booking?.admin_archived_at}
                    archiveReason={booking?.admin_archive_reason ?? null}
                  />
                </div>
              )}
            </div>

            {/* ── Body ─────────────────────────────────────────────────── */}
            <div className="p-6 space-y-5 bg-background">
              {/* Location row */}
              {(roomTitle || propertyTitle) && (
                <div className="flex items-center gap-2 text-sm">
                  <MapPin className="h-4 w-4 text-muted-foreground shrink-0" />
                  <span className="font-medium">{roomTitle ?? "—"}</span>
                  {propertyTitle && (
                    <span className="text-muted-foreground">
                      · {propertyTitle}
                    </span>
                  )}
                </div>
              )}

              {/* ── Lease dates & financials grid ────────────────────── */}
              <div className="grid grid-cols-2 gap-3">
                {/* Move-in */}
                <div className="rounded-2xl bg-muted/50 border border-border/50 p-3.5 space-y-1">
                  <div className="flex items-center gap-1.5 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
                    <Calendar className="h-3 w-3" />
                    {t("admin.remindersTab.moveIn", "Move-in")}
                  </div>
                  <div className="text-sm font-semibold text-foreground">
                    {formatDateProfile(moveIn)}
                  </div>
                </div>

                {/* Monthly rent */}
                <div className="rounded-2xl bg-emerald-50 border border-emerald-100 p-3.5 space-y-1">
                  <div className="flex items-center gap-1.5 text-[11px] font-semibold text-emerald-600 uppercase tracking-wider">
                    <Euro className="h-3 w-3" />
                    {t("admin.remindersTab.monthlyRent", "Monthly rent")}
                  </div>
                  <div className="text-sm font-bold text-emerald-700">
                    {booking?.monthly_rent != null
                      ? formatEur(Number(booking.monthly_rent))
                      : "—"}
                  </div>
                </div>

                {/* Move-out */}
                <div className="rounded-2xl bg-muted/50 border border-border/50 p-3.5 space-y-1">
                  <div className="flex items-center gap-1.5 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
                    <Calendar className="h-3 w-3" />
                    {t("admin.remindersTab.moveOut", "Move-out")}
                  </div>
                  <div className="text-sm font-semibold text-foreground">
                    {formatDateProfile(moveOut)}
                  </div>
                </div>

                {/* Deposit */}
                <div className="rounded-2xl bg-muted/50 border border-border/50 p-3.5 space-y-1">
                  <div className="flex items-center gap-1.5 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
                    <CreditCard className="h-3 w-3" />
                    {t("admin.remindersTab.deposit", "Deposit")}
                  </div>
                  <div className="text-sm font-semibold text-foreground">
                    {booking?.deposit_amount != null
                      ? formatEur(Number(booking.deposit_amount))
                      : "—"}
                  </div>
                </div>
              </div>

              {/* ── Contract row ─────────────────────────────────────── */}
              <div
                className={cn(
                  "flex items-center gap-3 rounded-2xl border px-4 py-3",
                  contractSigned
                    ? "bg-emerald-50 border-emerald-100"
                    : contractPdfUrl
                      ? "bg-sky-50 border-sky-100"
                      : "bg-muted/40 border-border/50"
                )}
              >
                <div
                  className={cn(
                    "h-8 w-8 rounded-xl flex items-center justify-center shrink-0",
                    contractSigned
                      ? "bg-emerald-100"
                      : contractPdfUrl
                        ? "bg-sky-100"
                        : "bg-muted"
                  )}
                >
                  <FileCheck
                    className={cn(
                      "h-4 w-4",
                      contractSigned
                        ? "text-emerald-600"
                        : contractPdfUrl
                          ? "text-sky-600"
                          : "text-muted-foreground"
                    )}
                  />
                </div>
                <div className="min-w-0">
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {t("admin.remindersTab.contract", "Contract")}
                  </div>
                  <div
                    className={cn(
                      "text-sm font-semibold",
                      contractSigned
                        ? "text-emerald-700"
                        : contractPdfUrl
                          ? "text-sky-700"
                          : "text-muted-foreground"
                    )}
                  >
                    {contractSigned
                      ? `${t("admin.remindersTab.signedOnPrefix", "Signed")} · ${formatDateProfile(contractSigned)}`
                      : contractPdfUrl
                        ? t(
                            "admin.remindersTab.manualUploadByAdmin",
                            "Manual upload by admin"
                          )
                        : t("admin.remindersTab.notSigned", "Not signed")}
                  </div>
                </div>
                {contractStatus && !(!contractSigned && contractPdfUrl) && (
                  <Badge
                    className={cn(
                      "ml-auto text-xs shrink-0",
                      contractStatus === "signed"
                        ? "bg-emerald-100 text-emerald-700 border-emerald-200"
                        : contractStatus === "ready_to_sign"
                          ? "bg-sky-100 text-sky-700 border-sky-200"
                          : contractStatus === "pending"
                            ? "bg-amber-100 text-amber-700 border-amber-200"
                            : "bg-muted text-muted-foreground"
                    )}
                  >
                    {contractStatus.replace(/_/g, " ")}
                  </Badge>
                )}
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Component
// ───────────────────────────────────────────────────────────────────────────

export function RemindersTab({ propertyId }: { propertyId: number }) {
  const queryClient = useQueryClient();
  const { user, isOwnerPortalOnly } = useAuth();
  const navigate = useNavigate();
  const { t } = useLanguage();

  // Owners reach this tab through PropertyDetail (useOwnerPropertyAccess) and
  // must see dunning state — stage badges, days overdue, accumulated fees —
  // read-only. Sending reminders, marking paid and toggling tenants stay
  // admin-only actions.
  const readOnly = isOwnerPortalOnly;

  const [search, setSearch] = useState("");

  // Default to "all_overdue" so the list shows only tenants who have missed
  // their due date. current_stage is computed from rent_due_date so this
  // works immediately without waiting for a cron job to flip payment_status.
  const [filter, setFilter] = useState<FilterKey>("all_overdue");
  const [sort, setSort] = useState<SortKey>("days_overdue");

  // Drill-down state — null means we're in list mode. Keyed by row (a booking
  // can have a current-period row and past-month rows), see TenantRow.row_key.
  const [openRowKey, setOpenRowKey] = useState<string | null>(null);

  // Profile modal state
  const [profileBookingId, setProfileBookingId] = useState<number | null>(null);

  // Modal state
  const [draftFor, setDraftFor] = useState<TenantRow | null>(null);
  const [draftSubject, setDraftSubject] = useState("");
  const [draftHtml, setDraftHtml] = useState("");
  const [draftText, setDraftText] = useState("");
  const [draftLang, setDraftLang] = useState<ReminderLang>("de");
  const [emailFor, setEmailFor] = useState<TenantRow | null>(null);
  const [emailSubject, setEmailSubject] = useState("");
  const [emailHtml, setEmailHtml] = useState("");
  const [emailText, setEmailText] = useState("");
  const [markPaidFor, setMarkPaidFor] = useState<TenantRow | null>(null);

  // ── Load every booking for this property + hydrate ──
  const {
    data: rows,
    isLoading,
    error: queryError,
    refetch,
  } = useQuery<TenantRow[]>({
    queryKey: ["property-reminders", "rows", propertyId],
    queryFn: async () => {
      const { data: roomsRes, error: rErr } = await supabase
        .from("rooms")
        .select("id, title, property_id, apartment_id")
        .eq("property_id", propertyId);
      if (rErr) throw new Error(`Could not load rooms: ${rErr.message}`);
      const rooms = (roomsRes ?? []) as Array<{
        id: number;
        title: string | null;
        property_id: number | null;
        apartment_id: number | null;
      }>;
      if (!rooms.length) return [];

      const roomIds = rooms.map((r) => r.id);
      const { data: bookingsData, error: bErr } = await supabase
        .from("bookings")
        .select(
          "id, user_id, room_id, monthly_rent, payment_status, rent_due_date, reminder_stage, reminder_sent_at, accumulated_fees_eur, inkasso_referred_at, last_payment_received_at, rent_paid_through, manually_marked_paid, status, start_date, end_date, admin_archived_at, actual_move_in_date, move_in_date, actual_move_out_date, move_out_date, rent_discount_mode, rent_discount_value, rent_discount_months, rent_discount_start_month"
        )
        .in("room_id", roomIds);
      if (bErr) throw new Error(`Could not load bookings: ${bErr.message}`);
      const bookings = (bookingsData ?? []) as unknown as Array<{
        id: number;
        user_id: string | null;
        room_id: number | null;
        monthly_rent: number | null;
        rent_discount_mode: string | null;
        rent_discount_value: number | null;
        rent_discount_months: number | null;
        rent_discount_start_month: string | null;
        payment_status: PaymentStatus | null;
        rent_due_date: number | null;
        reminder_stage: number | null;
        reminder_sent_at: string | null;
        accumulated_fees_eur: number | null;
        inkasso_referred_at: string | null;
        last_payment_received_at: string | null;
        rent_paid_through: string | null;
        manually_marked_paid: boolean | null;
        status: string | null;
        start_date: string | null;
        end_date: string | null;
        admin_archived_at: string | null;
        actual_move_in_date: string | null;
        move_in_date: string | null;
        actual_move_out_date: string | null;
        move_out_date: string | null;
      }>;
      if (!bookings.length) return [];

      const userIds = Array.from(
        new Set(bookings.map((b) => b.user_id).filter(Boolean) as string[])
      );
      const apartmentIds = Array.from(
        new Set(rooms.map((r) => r.apartment_id).filter(Boolean) as number[])
      );
      const [profilesRes, propertyRes, apartmentsRes] = await Promise.all([
        userIds.length
          ? supabase
              .from("profiles")
              .select("id, name, email")
              .in("id", userIds)
          : Promise.resolve({ data: [], error: null }),
        supabase
          .from("properties")
          .select(
            "id, title, city, rent_reminders_enabled, dunning_fee_per_stage_eur"
          )
          .eq("id", propertyId)
          .maybeSingle(),
        apartmentIds.length
          ? supabase
              .from("apartments")
              .select("id, rent_reminders_enabled")
              .in("id", apartmentIds)
          : Promise.resolve({ data: [], error: null }),
      ]);
      const apartmentRemindersById = new Map<number, boolean | null>();
      for (const a of (apartmentsRes.data ?? []) as Array<{
        id: number;
        rent_reminders_enabled: boolean | null;
      }>) {
        apartmentRemindersById.set(a.id, a.rent_reminders_enabled);
      }
      const profilesById = new Map<
        string,
        { name: string | null; email: string | null }
      >();
      for (const p of (profilesRes.data ?? []) as Array<{
        id: string;
        name: string | null;
        email: string | null;
      }>) {
        profilesById.set(p.id, { name: p.name, email: p.email });
      }
      const property = (propertyRes.data ?? null) as {
        id: number;
        title: string | null;
        city: string | null;
        rent_reminders_enabled: boolean | null;
        dunning_fee_per_stage_eur: number | string | null;
      } | null;
      const feePerStage = resolveDunningFee(
        property?.dunning_fee_per_stage_eur
      );
      const roomsById = new Map<
        number,
        { title: string | null; apartment_id: number | null }
      >();
      for (const r of rooms)
        roomsById.set(r.id, { title: r.title, apartment_id: r.apartment_id });

      const nowMs = Date.now();
      const out: TenantRow[] = bookings.map((b) => {
        const payStatus = (b.payment_status ?? "pending") as PaymentStatus;
        const rentDueDay = b.rent_due_date ?? null;

        const rawDaysOverdue = rentDueDay ? computeDaysOverdue(rentDueDay) : 0;
        const paidCurrentPeriod = isPaidCurrentPeriod(b);
        const room = b.room_id ? (roomsById.get(b.room_id) ?? null) : null;

        // Per-unit reminder suppression: apartment override wins, else the
        // property default, else on. Suppressed units are neutralised out of
        // overdue detection and shown as "Reminders off".
        const apartmentReminders =
          room?.apartment_id != null
            ? (apartmentRemindersById.get(room.apartment_id) ?? null)
            : null;
        const remindersEnabled =
          apartmentReminders ?? property?.rent_reminders_enabled ?? true;

        const daysOverdue =
          !remindersEnabled || paidCurrentPeriod ? 0 : rawDaysOverdue;
        const currentStage =
          !remindersEnabled || paidCurrentPeriod
            ? 0
            : computeCurrentStage(daysOverdue);
        const computedPayStatus = !remindersEnabled
          ? ("pending" as PaymentStatus)
          : paidCurrentPeriod
            ? ("paid" as PaymentStatus)
            : (paymentStatusForStage(currentStage) as PaymentStatus);
        // Keep notice-period tenants in collection until moved_out.
        const is_confirmed_active = isRentCollectionEligible(
          {
            status: b.status ?? "",
            admin_archived_at: b.admin_archived_at,
            actual_move_in_date: b.actual_move_in_date,
            move_in_date: b.move_in_date,
            start_date: b.start_date,
            actual_move_out_date: b.actual_move_out_date,
            move_out_date: b.move_out_date,
            end_date: b.end_date,
          },
          new Date(nowMs)
        );
        return {
          booking_id: b.id,
          user_id: b.user_id,
          room_id: b.room_id,
          // Discount-aware, same as AdminReminders: the totals and drafted
          // reminders in this tab all read this one field (COL-360).
          monthly_rent: effectiveRentForMonthEUR(
            b as unknown as Parameters<typeof effectiveRentForMonthEUR>[0],
            // The tenant's own billing period, not the calendar month: a due
            // day later than today rolls back to the previous month, and the
            // "Mark paid" ledger write reprices the pending row for THAT month.
            currentPeriodDueDate(rentDueDay ?? 1)
              .toISOString()
              .slice(0, 10)
          ),
          // Netted against this period's receipts once the ledger is in hand
          // (see `currentPeriodLedger` below) — the booking alone knows
          // what the month bills and nothing about what arrived.
          rent_billed_eur: effectiveRentForMonthEUR(
            b as unknown as Parameters<typeof effectiveRentForMonthEUR>[0],
            currentPeriodDueDate(rentDueDay ?? 1)
              .toISOString()
              .slice(0, 10)
          ),
          rent_received_eur: 0,
          payment_status: computedPayStatus,
          rent_due_date: rentDueDay,
          reminder_stage: (b.reminder_stage ?? 0) as 0 | 1 | 2 | 3 | 4,
          reminder_sent_at: b.reminder_sent_at,
          accumulated_fees_eur: Number(b.accumulated_fees_eur ?? 0),
          inkasso_referred_at: b.inkasso_referred_at,
          last_payment_received_at: b.last_payment_received_at,
          tenant_name: b.user_id
            ? (profilesById.get(b.user_id)?.name ?? null)
            : null,
          tenant_email: b.user_id
            ? (profilesById.get(b.user_id)?.email ?? null)
            : null,
          room_title: room?.title ?? null,
          property_title: property?.title ?? null,
          property_city: property?.city ?? null,
          days_overdue: daysOverdue,
          current_stage: currentStage,
          is_confirmed_active,
          reminders_enabled: remindersEnabled,
          fee_per_stage_eur: feePerStage,
          row_key: String(b.id),
          booking_fees_eur: Number(b.accumulated_fees_eur ?? 0),
          period_kind: "current",
          period_due_date: null,
          invoice_due_date: null,
          ledger_payment_id: null,
        };
      });

      // ── Past months (COL-435) ──
      // Rent from billing periods before each tenant's current one that is
      // still open in the ledger — one extra row per month, for the tenants
      // this tab tracks (occupying now, reminders on), i.e. the same set the
      // property badge counts.
      const tracked = out.filter(
        (r) => r.is_confirmed_active && r.reminders_enabled
      );
      if (tracked.length) {
        const trackedIds = new Set(tracked.map((r) => r.booking_id));
        const { ledger, proofFor } = await fetchRentLedgerForBookings(
          supabase,
          Array.from(trackedIds)
        );
        const baseByBooking = new Map(tracked.map((r) => [r.booking_id, r]));
        const pastMonths = selectOpenPastMonthRent({
          rows: ledger,
          proofFor,
          rentDueDayByBooking: new Map(
            tracked.map((r) => [r.booking_id, r.rent_due_date])
          ),
          bookingIds: trackedIds,
          now: new Date(nowMs),
        });
        // COL-417: the current period is the one row not built from a ledger
        // row, so without this it stays the only line on the screen still
        // demanding the full rent after a partial payment. Reads the ledger
        // that was just fetched — no extra query.
        const currentNow = currentPeriodLedger(
          ledger,
          new Map(tracked.map((r) => [r.booking_id, r.rent_due_date])),
          new Date(nowMs)
        );
        const visibleRows = keepInvoicedCurrentRows(out, currentNow, trackedIds);
        out.splice(0, out.length, ...visibleRows);
        for (const r of tracked) {
          const cur = currentNow.get(r.booking_id);
          if (!cur) continue;
          r.invoice_due_date = cur.dueDate;
          if (r.payment_status !== "paid") {
            r.days_overdue = daysSince(cur.dueDate, new Date(nowMs));
            r.current_stage = computeCurrentStage(r.days_overdue);
            r.payment_status = paymentStatusForStage(
              r.current_stage
            ) as PaymentStatus;
          }
          // Billed AND received both come from the current month's ledger row.
          // Netting the ledger's received against the booking's rent is what
          // silently zeroed a row whose month had an edited `amount_cents`
          // (EUR 800 invoiced, EUR 650 booking, EUR 700 in), and over-demanded
          // on the reverse edit. Where a ledger row exists it is the invoice,
          // exactly as it already is for every past-month row below.
          const billedEur =
            cur.billedCents > 0 ? cur.billedCents / 100 : r.rent_billed_eur;
          r.rent_billed_eur = billedEur;
          r.rent_received_eur = cur.receivedCents / 100;
          r.monthly_rent = Math.max(0, billedEur - cur.receivedCents / 100);
        }
        // Place booking-level fees only after the concrete invoice date has
        // recalculated the current row's overdue state.
        const { currentRowFees, pastRowFees } = placeBookingFees(
          currentRowsForFeePlacement(tracked, currentNow),
          pastMonths
        );
        for (const r of tracked) {
          const fees = currentRowFees.get(r.booking_id);
          if (fees !== undefined) r.accumulated_fees_eur = fees;
        }
        for (const m of pastMonths) {
          const base = baseByBooking.get(m.booking_id);
          if (!base) continue;
          out.push({
            ...base,
            row_key: `${m.booking_id}:${m.ym}`,
            period_kind: "past",
            period_due_date: m.due_date,
            invoice_due_date: null,
            ledger_payment_id: m.payment_id,
            // That month's own invoice amount, less anything received
            // against it (COL-417) — `open_eur` equals `amount_eur` for every
            // month nobody part-paid.
            monthly_rent: m.open_eur,
            rent_billed_eur: m.amount_eur,
            rent_received_eur: m.received_eur,
            accumulated_fees_eur: pastRowFees.get(m.payment_id) ?? 0,
            // Open by construction; the booking's own claim may read "paid"
            // because the CURRENT period is covered.
            payment_status: "pending",
            days_overdue: m.days_overdue,
            // Next step in the booking's dunning chain (product decision:
            // continue from the stored stage rather than recomputing from a
            // due date that is always 9+ days back).
            current_stage: nextStageInChain(base.reminder_stage),
          });
        }
      }
      return out;
    },
  });

  const openTenant = openRowKey
    ? ((rows ?? []).find((r) => r.row_key === openRowKey) ?? null)
    : null;
  const openBookingId = openTenant?.booking_id ?? null;

  // ── Activity for the open tenant ──
  const { data: activity } = useQuery({
    queryKey: ["property-reminders", "activity", openBookingId],
    enabled: openBookingId != null,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("payments_activity")
        .select(
          "id, event_type, stage, amount_eur, email_lang, note, email_to, email_subject, email_html, email_text, created_at, created_by"
        )
        .eq("booking_id", openBookingId)
        .order("created_at", { ascending: false });
      if (error) {
        console.warn("payments_activity load failed:", error);
        return [] as ActivityRow[];
      }
      return (data ?? []) as ActivityRow[];
    },
  });

  // ── Counts per filter chip ──
  // All filters key off `current_stage` (computed from days_overdue), with
  // `payment_status === 'paid'` as a hard short-circuit. This way a tenant
  // whose `payment_status` is still `pending` but who is in fact 5 days
  // overdue is correctly counted under "Stage 2–3" — the cron flipping the
  // column is just a nicety, not a prerequisite.
  // Stage chips describe the CURRENT period's dunning cycle; past-month rows
  // count under "Overdue" (they are) and "Past months", never under a stage.
  const counts = useMemo(() => {
    const list = (rows ?? []).filter((r) => r.is_confirmed_active);
    const current = list.filter((r) => r.period_kind === "current");
    const total = list.length;
    const all_overdue = list.filter(
      (r) => r.payment_status !== "paid" && r.current_stage > 0
    ).length;
    const stage_1 = current.filter(
      (r) => r.payment_status !== "paid" && r.current_stage === 1
    ).length;
    const stage_2_3 = current.filter(
      (r) =>
        r.payment_status !== "paid" &&
        (r.current_stage === 2 || r.current_stage === 3)
    ).length;
    const inkasso = current.filter(
      (r) => r.payment_status !== "paid" && r.current_stage === 4
    ).length;
    const now = new Date();
    const monthStart = new Date(
      Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1)
    ).getTime();
    const paid_this_month = current.filter((r) => {
      if (r.payment_status !== "paid" || !r.last_payment_received_at)
        return false;
      return Date.parse(r.last_payment_received_at) >= monthStart;
    }).length;
    const past_months = list.filter((r) => r.period_kind === "past").length;
    return {
      total,
      all_overdue,
      stage_1,
      stage_2_3,
      inkasso,
      paid_this_month,
      past_months,
    };
  }, [rows]);

  const filterPasses = (r: TenantRow): boolean => {
    if (filter === "all") return true;
    if (filter === "all_overdue")
      return r.payment_status !== "paid" && r.current_stage > 0;
    if (filter === "past_months") return r.period_kind === "past";
    if (r.period_kind !== "current") return false;
    if (filter === "stage_1")
      return r.payment_status !== "paid" && r.current_stage === 1;
    if (filter === "stage_2_3")
      return (
        r.payment_status !== "paid" &&
        (r.current_stage === 2 || r.current_stage === 3)
      );
    if (filter === "inkasso")
      return r.payment_status !== "paid" && r.current_stage === 4;
    if (filter === "paid_this_month") {
      if (r.payment_status !== "paid" || !r.last_payment_received_at)
        return false;
      const now = new Date();
      const monthStart = new Date(
        Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1)
      ).getTime();
      return Date.parse(r.last_payment_received_at) >= monthStart;
    }
    return true;
  };

  const filteredSorted = useMemo(() => {
    const q = search.trim().toLowerCase();
    const arr = (rows ?? [])
      .filter((r) => r.is_confirmed_active)
      .filter((r) => filterPasses(r))
      .filter((r) => {
        if (!q) return true;
        return (
          (r.tenant_name ?? "").toLowerCase().includes(q) ||
          (r.tenant_email ?? "").toLowerCase().includes(q) ||
          (r.room_title ?? "").toLowerCase().includes(q) ||
          (r.property_title ?? "").toLowerCase().includes(q) ||
          (r.property_city ?? "").toLowerCase().includes(q) ||
          String(r.booking_id).includes(q)
        );
      });
    arr.sort((a, b) => {
      if (sort === "name")
        return (a.tenant_name ?? "").localeCompare(b.tenant_name ?? "");
      if (sort === "amount")
        return (
          b.monthly_rent +
          b.accumulated_fees_eur -
          (a.monthly_rent + a.accumulated_fees_eur)
        );
      return b.days_overdue - a.days_overdue;
    });
    return arr;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, filter, sort, search]);

  const summary = useMemo(() => {
    // Summary only counts active tenants (confirmed, current lease, not manually hidden).
    const activeRows = (rows ?? []).filter((r) => r.is_confirmed_active);
    // Overdue € includes open past months (COL-435); "tenants" stays a head
    // count, so a tenant with two open months is one tenant.
    const overdueBookings = new Set<number>();
    let overdueTotalEur = 0;
    let overdueFeesEur = 0;

    for (const r of activeRows) {
      if (r.payment_status === "paid") continue;
      if (r.current_stage <= 0) continue;
      overdueBookings.add(r.booking_id);
      overdueTotalEur += r.monthly_rent + r.accumulated_fees_eur;
      overdueFeesEur += r.accumulated_fees_eur;
    }
    const overdueTenants = overdueBookings.size;

    // Rent-domain counts (room occupancy is a property concept shown in the
    // sidebar, not derivable from bookings without impossible ratios).
    // Reminders-off (owner-collected) units are excluded — we don't track their
    // payment, so counting them would make the "On time" ratio misleading.
    const trackedRows = activeRows.filter(
      (r) => r.period_kind === "current" && r.reminders_enabled
    );
    const tenants = trackedRows.length;
    const onTime = trackedRows.filter(
      (r) => r.payment_status === "paid"
    ).length;
    return {
      tenants,
      onTime,
      overdueTenants,
      overdueTotalEur,
      overdueFeesEur,
    };
  }, [rows]);

  // ── Email preview (recomputed when target row OR language toggles) ──
  const draftPreview = useMemo(() => {
    if (!draftFor) return null;
    const stage = (
      draftFor.current_stage > 0 ? draftFor.current_stage : 1
    ) as ReminderStage;
    const today = new Date();
    const periodDue = rowPeriodDue(draftFor, today);
    // Per-property Mahngebühr (COL-308) — the preview must show the same fee
    // the edge function renders into the sent email.
    const feePerStage = draftFor.fee_per_stage_eur;
    const feeCount =
      stage === 1
        ? 0
        : stage === 2
          ? 1
          : stage === 3
            ? 2
            : Math.max(
                3,
                feePerStage > 0
                  ? Math.round(draftFor.booking_fees_eur / feePerStage) + 1
                  : 3
              );
    const stage3Deadline = new Date(today);
    stage3Deadline.setUTCDate(stage3Deadline.getUTCDate() + 2);
    const monthLabel =
      draftLang === "en" ? monthLabelEn(periodDue) : monthLabelDe(periodDue);
    const dueDate =
      draftLang === "en" ? formatDateEn(periodDue) : formatDateDe(periodDue);
    const stage3Deadline2 =
      draftLang === "en"
        ? formatDateEn(stage3Deadline)
        : formatDateDe(stage3Deadline);

    // Derive fee dates: past fees from activity history (reminder_sent with
    // stage >= 2), sorted oldest-first, then today for the current send.
    // If activity records are missing for earlier stages (e.g. test/skipped),
    // fall back to today so every fee row always shows a date.
    const fmt = draftLang === "en" ? formatDateEn : formatDateDe;
    const todayFmt = fmt(today);
    const pastFeeDates = (activity ?? [])
      .filter((a) => a.event_type === "reminder_sent" && (a.stage ?? 0) >= 2)
      .sort((a, b) => a.created_at.localeCompare(b.created_at))
      .map((a) => fmt(new Date(a.created_at)));
    const feeDates =
      feeCount > 0
        ? Array.from({ length: feeCount }, (_, i) =>
            i < feeCount - 1 ? (pastFeeDates[i] ?? todayFmt) : todayFmt
          )
        : undefined;

    const rendered = renderReminderEmail(
      stage,
      {
        firstName:
          (draftFor.tenant_name ?? "").trim().split(/\s+/)[0] || "Tenant",
        fullName: draftFor.tenant_name?.trim() || "Tenant",
        room: draftFor.room_title?.trim() || `Booking #${draftFor.booking_id}`,
        month: monthLabel,
        dueDate,
        rentEur: draftFor.monthly_rent,
        feeCount,
        rentDueDay: draftFor.rent_due_date ?? undefined,
        feeDates,
        stage3DeadlineDate: stage3Deadline2,
        feePerStage,
      },
      draftLang
    );
    return { ...rendered, stage, feeCount };
  }, [draftFor, draftLang, activity]);

  useEffect(() => {
    if (draftPreview) setDraftSubject(draftPreview.subject);
  }, [draftPreview]);

  useEffect(() => {
    if (!draftPreview) return;
    setDraftHtml(draftPreview.html);
    setDraftText(draftPreview.text);
  }, [draftPreview]);

  // ── Mutations ──
  const sendReminder = useMutation({
    mutationFn: async () => {
      if (!draftFor || !draftPreview)
        throw new Error(
          t("owner.reminders.toast.noDraftSelected", "No draft selected")
        );
      const session = await supabase.auth.getSession();
      const token = session.data.session?.access_token ?? "";
      const { data, error } = await supabase.functions.invoke(
        "send-rent-reminder",
        {
          body: {
            bookingId: draftFor.booking_id,
            stage: draftPreview.stage,
            // Past-month row: the edge function validates the open ledger
            // row and renders that month (COL-435).
            periodDueDate: draftFor.period_due_date ?? undefined,
            subjectOverride: draftSubject || draftPreview.subject,
            htmlOverride: draftHtml || draftPreview.html,
            textOverride: draftText || draftPreview.text,
            email_lang: draftLang,
          },
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        }
      );
      if (error)
        // supabase-js collapses a non-2xx into the fixed string "Edge Function
        // returned a non-2xx status code" and leaves the real reason
        // ("Admin access required", "Stage mismatch — …") unread on error.context.
        throw new Error(
          await edgeFunctionErrorMessage(
            error,
            t(
              "owner.reminders.toast.couldNotSendReminder",
              "Could not send reminder"
            )
          )
        );
      return data;
    },
    onSuccess: () => {
      toast.success(t("owner.reminders.toast.reminderSent", "Reminder sent"));
      setDraftFor(null);
      queryClient.invalidateQueries({ queryKey: ["property-reminders"] });
      queryClient.invalidateQueries({
        queryKey: [PROPERTY_REMINDER_STATS_KEY],
      });
    },
    onError: (e) =>
      toast.error(
        e instanceof Error
          ? e.message
          : t(
              "owner.reminders.toast.couldNotSendReminder",
              "Could not send reminder"
            )
      ),
  });

  const saveDraftActivity = useMutation({
    mutationFn: async () => {
      if (!draftFor || !draftPreview)
        throw new Error(
          t("owner.reminders.toast.noDraftSelected", "No draft selected")
        );
      await supabase.from("payments_activity").insert({
        booking_id: draftFor.booking_id,
        event_type: "reminder_draft_saved",
        stage: draftPreview.stage,
        amount_eur: null,
        email_lang: draftLang,
        note: `${draftSubject || draftPreview.subject}\n\n${draftText || draftPreview.text}`.trim(),
        email_to: draftFor.tenant_email ?? null,
        email_subject: draftSubject || draftPreview.subject,
        email_html: draftHtml || draftPreview.html,
        email_text: draftText || draftPreview.text,
        created_by: user?.id ?? null,
      });
    },
    onSuccess: () => {
      toast.success(
        t("owner.reminders.toast.draftSaved", "Draft saved to timeline")
      );
      queryClient.invalidateQueries({
        queryKey: ["property-reminders", "activity", draftFor?.booking_id],
      });
    },
    onError: (e) =>
      toast.error(
        e instanceof Error
          ? e.message
          : t("owner.reminders.toast.couldNotSaveDraft", "Could not save draft")
      ),
  });

  const sendAdminEmail = useMutation({
    mutationFn: async () => {
      if (!emailFor)
        throw new Error(
          t("admin.remindersTab.toast.noTenantSelected", "No tenant selected")
        );
      const to = emailFor.tenant_email?.trim() ?? "";
      if (!to)
        throw new Error(
          t("admin.remindersTab.toast.tenantHasNoEmail", "Tenant has no email")
        );
      const session = await supabase.auth.getSession();
      const token = session.data.session?.access_token ?? "";
      const { data, error } = await supabase.functions.invoke(
        "send-admin-email",
        {
          body: {
            to,
            subject: emailSubject.trim(),
            html: emailHtml.trim(),
            text: emailText.trim(),
          },
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        }
      );
      if (error)
        // Same unwrap as sendReminder: the real reason ("Forbidden",
        // "Missing to/subject") is on error.context, not on error.message.
        throw new Error(
          await edgeFunctionErrorMessage(
            error,
            t(
              "admin.remindersTab.toast.couldNotSendEmail",
              "Could not send email"
            )
          )
        );
      return data;
    },
    onSuccess: () => {
      toast.success(t("admin.remindersTab.toast.emailSent", "Email sent"));
      // Also log into the payments timeline for this tenant/booking.
      if (emailFor?.booking_id) {
        supabase.from("payments_activity").insert({
          booking_id: emailFor.booking_id,
          event_type: "admin_email_sent",
          stage: emailFor.current_stage || null,
          amount_eur: null,
          email_lang: null,
          note: `${emailSubject.trim()}\n\n${(emailText || "").trim()}`.trim(),
          email_to: emailFor.tenant_email ?? null,
          email_subject: emailSubject.trim() || null,
          email_html: emailHtml.trim() || null,
          email_text: emailText.trim() || null,
          created_by: user?.id ?? null,
        });
      }
      setEmailFor(null);
      setEmailSubject("");
      setEmailHtml("");
      setEmailText("");
    },
    onError: (e) =>
      toast.error(
        e instanceof Error
          ? e.message
          : t(
              "admin.remindersTab.toast.couldNotSendEmail",
              "Could not send email"
            )
      ),
  });

  const markPaid = useMutation({
    mutationFn: async () => {
      if (!markPaidFor)
        throw new Error(
          t("owner.reminders.toast.noBookingSelected", "No booking selected")
        );
      const totalOwed =
        markPaidFor.monthly_rent + markPaidFor.accumulated_fees_eur;
      const nowIso = new Date().toISOString();
      if (markPaidFor.period_kind === "past") {
        // A past month: settle THAT ledger row through the same proof-gated
        // pathway and log it. The booking's payment columns describe the
        // current period and stay untouched here; the payments→bookings sync
        // trigger recomputes rent_paid_through from the ledger.
        const { error: ledgerErr } = await recordManualRentPaidInLedger({
          bookingId: markPaidFor.booking_id,
          tenantUserId: markPaidFor.user_id,
          rentDueDay: markPaidFor.rent_due_date,
          monthlyRentEur: markPaidFor.monthly_rent,
          adminUserId: user?.id ?? null,
          periodDueDate: markPaidFor.period_due_date,
          // The row already exists (it's why this month showed as open) —
          // settle it directly so a manual/shell booking with no
          // `user_id` still gets its ledger row marked paid instead of
          // this silently no-op-ing (COL-435).
          paymentId: markPaidFor.ledger_payment_id,
        });
        if (ledgerErr) throw ledgerErr;
        if (markPaidFor.accumulated_fees_eur > 0) {
          // This row carried the booking's Mahngebühren (no overdue current
          // row); the dialog told the admin fees reset on confirmation.
          const { error: feeErr } = await supabase
            .from("bookings")
            .update({ accumulated_fees_eur: 0 })
            .eq("id", markPaidFor.booking_id);
          if (feeErr) throw feeErr;
        }
        await supabase.from("payments_activity").insert({
          booking_id: markPaidFor.booking_id,
          event_type: "marked_paid",
          amount_eur: totalOwed,
          stage: markPaidFor.current_stage || null,
          note: `Past month ${markPaidFor.period_due_date?.slice(0, 7) ?? ""} marked paid by admin.`,
          created_by: user?.id ?? null,
        });
        return;
      }
      // Ledger first (COL-227): record the payment in `payments` through the
      // proof-gated pathway so PaymentsHub / tenant profile / Dashboard /
      // analytics agree with the reminders view. Failing here aborts before
      // the booking columns are touched — no silent drift.
      const { error: ledgerErr } = await recordManualRentPaidInLedger({
        bookingId: markPaidFor.booking_id,
        tenantUserId: markPaidFor.user_id,
        rentDueDay: markPaidFor.rent_due_date,
        monthlyRentEur: markPaidFor.monthly_rent,
        adminUserId: user?.id ?? null,
      });
      if (ledgerErr) throw ledgerErr;
      // Gap-aware, mirroring recompute_booking_rent_coverage (COL-435): only
      // reset the dunning chain/fee pool when nothing else is outstanding.
      // Zeroing them while a past month stays open would regress its
      // `nextStageInChain` back to stage 1 and wipe the fees that
      // `placeBookingFees` is about to shift onto that oldest past row.
      const hasOpenPastMonth = (rows ?? []).some(
        (r) =>
          r.booking_id === markPaidFor.booking_id && r.period_kind === "past"
      );
      const { error } = await supabase
        .from("bookings")
        .update({
          payment_status: "paid",
          last_payment_received_at: nowIso,
          ...(hasOpenPastMonth
            ? {}
            : { reminder_stage: 0, accumulated_fees_eur: 0 }),
        })
        .eq("id", markPaidFor.booking_id);
      if (error) throw error;
      await supabase.from("payments_activity").insert({
        booking_id: markPaidFor.booking_id,
        event_type: "marked_paid",
        amount_eur: totalOwed,
        stage: markPaidFor.current_stage || null,
        created_by: user?.id ?? null,
      });
    },
    onSuccess: () => {
      toast.success(t("owner.reminders.toast.markedAsPaid", "Marked as paid"));
      setMarkPaidFor(null);
      queryClient.invalidateQueries({ queryKey: ["property-reminders"] });
      queryClient.invalidateQueries({
        queryKey: [PROPERTY_REMINDER_STATS_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: [PROPERTY_RENT_PAID_STATS_KEY],
      });
      // A rent payment settling can flip a company_collects group's payout
      // readiness (COL-461) — the owners-list stat tile reads this too.
      invalidateOwnerCaches(queryClient);
      queryClient.invalidateQueries({ queryKey: ["active-booking"] });
      queryClient.invalidateQueries({ queryKey: ["tenant-all-bookings"] });
      queryClient.invalidateQueries({ queryKey: ["tenant-approved-bookings"] });
      queryClient.invalidateQueries({ queryKey: ["payments"] });
      queryClient.invalidateQueries({ queryKey: ["payments-upcoming"] });
      invalidateOccupancyViews(queryClient);
      queryClient.invalidateQueries({ queryKey: ["admin-bookings"] });
      queryClient.invalidateQueries({ queryKey: ["admin-payments"] });
      queryClient.invalidateQueries({ queryKey: ["admin-reminders"] });
      queryClient.invalidateQueries({ queryKey: ["tenant-payments"] });
      queryClient.invalidateQueries({ queryKey: ["tenant-activity-payments"] });
    },
    onError: (e) =>
      toast.error(
        e instanceof Error
          ? e.message
          : t("owner.reminders.toast.couldNotMarkPaid", "Could not mark paid")
      ),
  });

  // ───────────────────────────────────────────────────────────────────────
  // UI atoms
  // ───────────────────────────────────────────────────────────────────────

  const FilterChip = ({
    k,
    label,
    title,
  }: {
    k: FilterKey;
    label: string;
    title?: string;
  }) => {
    const active = filter === k;
    const count =
      k === "all"
        ? counts.total
        : k === "all_overdue"
          ? counts.all_overdue
          : k === "stage_1"
            ? counts.stage_1
            : k === "stage_2_3"
              ? counts.stage_2_3
              : k === "inkasso"
                ? counts.inkasso
                : k === "past_months"
                  ? counts.past_months
                  : counts.paid_this_month;
    return (
      <button
        type="button"
        title={title}
        onClick={() => setFilter(k)}
        className={cn(
          "inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm transition-colors",
          active
            ? "bg-foreground text-background shadow-sm"
            : "bg-transparent text-muted-foreground hover:bg-muted/60"
        )}
      >
        {label}
        <span
          className={cn(
            "inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[11px] font-semibold tabular-nums",
            active
              ? "bg-background/20 text-background"
              : "bg-muted/70 text-foreground/70"
          )}
        >
          {count}
        </span>
      </button>
    );
  };

  const SortDropdown = () => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="rounded-full h-9 gap-1.5"
        >
          <ArrowDownUp className="h-3.5 w-3.5 opacity-60" />
          {sort === "days_overdue"
            ? t("owner.reminders.sort.daysOverdue", "Sort by days overdue")
            : sort === "amount"
              ? t("owner.reminders.sort.amount", "Sort by amount")
              : t("owner.reminders.sort.name", "Sort by name")}
          <ChevronDown className="h-3.5 w-3.5 opacity-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => setSort("days_overdue")}>
          {t("owner.reminders.sortOpt.daysOverdue", "Days overdue")}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => setSort("amount")}>
          {t("owner.reminders.sortOpt.amount", "Amount owed")}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => setSort("name")}>
          {t("owner.reminders.sortOpt.name", "Tenant name")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  // Clicking a tenant name should open the full tenant overview page rather
  // than the small inline profile dialog (COL-253). We resolve the room from
  // the row so we can build the canonical tenant-detail route; if the booking
  // has no room association we fall back to the dialog.
  const openTenantOverview = (bookingId: number) => {
    const row = (rows ?? []).find((r) => r.booking_id === bookingId);
    if (row?.room_id != null) {
      navigate(
        `/admin/properties/${propertyId}/rooms/${row.room_id}/tenants/${bookingId}`
      );
    } else {
      setProfileBookingId(bookingId);
    }
  };

  // ───────────────────────────────────────────────────────────────────────
  // Render — list view OR detail view
  // ───────────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-4">
      {openTenant ? (
        <DetailView
          row={openTenant}
          activity={activity ?? []}
          readOnly={readOnly}
          onBack={() => setOpenRowKey(null)}
          onDraft={() => setDraftFor(openTenant)}
          onMarkPaid={() => setMarkPaidFor(openTenant)}
          onOpenProfile={(id) => openTenantOverview(id)}
        />
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <div className="rounded-2xl border border-sky-200/70 bg-sky-50/40 p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">
                    {t("owner.reminders.statTenants", "Tenants tracked")}
                  </div>
                  <div className="text-2xl font-bold text-foreground tabular-nums mt-1">
                    {summary.tenants}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {t(
                      "admin.remindersTab.activeTenantsInProperty",
                      "Active tenants in this property"
                    )}
                  </div>
                </div>
                <div className="h-10 w-10 rounded-2xl bg-white/70 border border-sky-200 flex items-center justify-center text-sky-700">
                  <UsersIcon className="h-5 w-5" />
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-emerald-200/70 bg-emerald-50/40 p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">
                    {t("owner.reminders.statOnTime", "On time")}
                  </div>
                  <div className="text-2xl font-bold text-foreground tabular-nums mt-1">
                    {summary.onTime}
                    <span className="text-sm text-muted-foreground font-medium">
                      {" "}
                      / {summary.tenants}
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {t(
                      "owner.reminders.statOnTimeHint",
                      "Paid for the current period"
                    )}
                  </div>
                </div>
                <div className="h-10 w-10 rounded-2xl bg-white/70 border border-emerald-200 flex items-center justify-center text-emerald-700">
                  <CheckCircle2 className="h-5 w-5" />
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-rose-200/70 bg-rose-50/40 p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">
                    {t("owner.reminders.statOverdue", "Overdue")}
                  </div>
                  <div className="text-2xl font-bold text-rose-800 tabular-nums mt-1">
                    {formatEur(summary.overdueTotalEur)}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {summary.overdueTenants}{" "}
                    {t("owner.reminders.tenantsWord", "tenants")} ·{" "}
                    {formatEur(summary.overdueFeesEur)}{" "}
                    {t("owner.reminders.inFeesPhrase", "in fees")}
                  </div>
                </div>
                <div className="h-10 w-10 rounded-2xl bg-white/70 border border-rose-200 flex items-center justify-center text-rose-700">
                  <TriangleAlert className="h-5 w-5" />
                </div>
              </div>
            </div>
          </div>

          <ListView
            rows={rows ?? []}
            filteredSorted={filteredSorted}
            isLoading={isLoading}
            queryError={queryError}
            readOnly={readOnly}
            onRetry={() => refetch()}
            onOpenTenant={(key) => setOpenRowKey(key)}
            onOpenProfile={(id) => openTenantOverview(id)}
            onDraft={(r) => setDraftFor(r)}
            FilterChipNode={FilterChip}
            SortNode={SortDropdown}
            search={search}
            setSearch={setSearch}
          />
        </>
      )}

      <TenantProfileDialog
        bookingId={profileBookingId}
        onClose={() => setProfileBookingId(null)}
      />

      <EmailDraftDialog
        row={draftFor}
        preview={draftPreview}
        subject={draftSubject}
        setSubject={setDraftSubject}
        html={draftHtml}
        setHtml={setDraftHtml}
        text={draftText}
        setText={setDraftText}
        lang={draftLang}
        setLang={setDraftLang}
        onClose={() => setDraftFor(null)}
        onSaveDraft={() => saveDraftActivity.mutate()}
        onSend={() => sendReminder.mutate()}
        sending={sendReminder.isPending}
      />

      <AdminEmailDialog
        row={emailFor}
        subject={emailSubject}
        setSubject={setEmailSubject}
        html={emailHtml}
        setHtml={setEmailHtml}
        text={emailText}
        setText={setEmailText}
        onClose={() => setEmailFor(null)}
        onSend={() => sendAdminEmail.mutate()}
        sending={sendAdminEmail.isPending}
      />

      <MarkPaidDialog
        row={markPaidFor}
        onClose={() => setMarkPaidFor(null)}
        onConfirm={() => markPaid.mutate()}
        confirming={markPaid.isPending}
      />
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// List view
// ───────────────────────────────────────────────────────────────────────────

function ListView({
  rows,
  filteredSorted,
  isLoading,
  queryError,
  readOnly = false,
  onRetry,
  onOpenTenant,
  onOpenProfile,
  onDraft,
  FilterChipNode,
  SortNode,
  search,
  setSearch,
}: {
  rows: TenantRow[];
  filteredSorted: TenantRow[];
  isLoading: boolean;
  queryError: unknown;
  /** Owner-portal viewers see dunning state read-only — no send/toggle. */
  readOnly?: boolean;
  onRetry: () => void;
  onOpenTenant: (rowKey: string) => void;
  onOpenProfile: (bookingId: number) => void;
  onDraft: (r: TenantRow) => void;
  FilterChipNode: (props: {
    k: FilterKey;
    label: string;
    title?: string;
  }) => JSX.Element;
  SortNode: () => JSX.Element;
  search: string;
  setSearch: (v: string) => void;
}) {
  const { t, language } = useLanguage();
  const PAGE_SIZE = 5;
  const [page, setPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(filteredSorted.length / PAGE_SIZE));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const pageRows = filteredSorted.slice(
    (safePage - 1) * PAGE_SIZE,
    safePage * PAGE_SIZE
  );

  useEffect(() => {
    setPage(1);
  }, [filteredSorted]);

  return (
    <>
      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t(
            "admin.remindersTab.searchPlaceholder",
            "Search by tenant, email, room, or booking…"
          )}
          className="pl-9 h-11 rounded-2xl bg-background/70 border-border/60 shadow-sm focus-visible:ring-2 focus-visible:ring-emerald-500/20"
        />
        {search ? (
          <button
            type="button"
            onClick={() => setSearch("")}
            className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 rounded-xl hover:bg-muted flex items-center justify-center text-muted-foreground"
            aria-label={t("owner.reminders.clearAria", "Clear")}
          >
            <X className="h-4 w-4" />
          </button>
        ) : null}
      </div>

      {/* Filter chips + sort */}
      <div className="flex flex-wrap items-center gap-2 justify-between">
        <div className="flex flex-wrap items-center gap-1">
          <FilterChipNode
            k="all"
            label={t("owner.reminders.filterAllTenants", "All")}
          />
          <FilterChipNode
            k="all_overdue"
            label={t("owner.reminders.filterAllOverdue", "Overdue")}
          />
          <FilterChipNode
            k="stage_1"
            label={t("owner.reminders.stage1", "1st reminder")}
          />
          <FilterChipNode
            k="stage_2_3"
            label={t("owner.reminders.filterStage23", "2nd–3rd reminder")}
          />
          <FilterChipNode
            k="inkasso"
            label={t("owner.reminders.filterInkasso", "Final notice")}
          />
          <FilterChipNode
            k="paid_this_month"
            label={t("owner.reminders.filterPaidMonth", "Paid this month")}
          />
          <FilterChipNode
            k="past_months"
            label={t("owner.reminders.filterPastMonths", "Past months")}
            title={t(
              "owner.reminders.filterPastMonthsTooltip",
              "Outstanding payments from previous billing periods"
            )}
          />
        </div>
        <div className="flex items-center gap-2">
          <SortNode />
        </div>
      </div>

      {/* Error banner */}
      {queryError ? (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-rose-700 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-rose-900">
              {t("owner.reminders.loadError", "Couldn't load reminders")}
            </div>
            <div className="text-xs text-rose-800 mt-1 break-words">
              {queryError instanceof Error
                ? queryError.message
                : String(queryError)}
            </div>
          </div>
          <Button
            size="sm"
            variant="outline"
            className="rounded-full h-8 text-xs border-rose-300 text-rose-700 hover:bg-rose-100"
            onClick={onRetry}
          >
            {t("owner.reminders.retry", "Retry")}
          </Button>
        </div>
      ) : null}

      {/* Table (hidden when empty) */}
      {isLoading || filteredSorted.length > 0 ? (
        <Card className="overflow-hidden">
          <div className="hidden lg:grid grid-cols-[minmax(0,1.7fr)_minmax(0,1.2fr)_minmax(0,1.9fr)_minmax(0,0.85fr)_minmax(0,1.05fr)] gap-3 px-5 py-3 border-b border-border/60 bg-muted/30 text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">
            <div>{t("owner.reminders.colTenant", "Tenant")}</div>
            <div>{t("admin.remindersTab.colRoom", "Room")}</div>
            <div>{t("owner.reminders.colStatus", "Status")}</div>
            <div>{t("owner.reminders.colDue", "Due")}</div>
            <div className="text-center">
              {t("admin.remindersTab.colAction", "Action")}
            </div>
          </div>

          {isLoading ? (
            <div className="py-16 flex items-center justify-center text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
              {t("owner.reminders.loading", "Loading…")}
            </div>
          ) : (
            <ul className="divide-y divide-border/60">
              {pageRows.map((r) => {
                const totalOwed = r.monthly_rent + r.accumulated_fees_eur;
                const isPast = r.period_kind === "past";
                const moveOutDue =
                  isPast || r.rent_due_date != null ? rowPeriodDue(r) : null;
                const { sentLine, nextLine, nextMissed } =
                  reminderScheduleLines({
                    reminders_enabled: r.reminders_enabled,
                    paid: r.payment_status === "paid",
                    rent_due_date: r.rent_due_date,
                    reminder_stage: r.reminder_stage,
                    reminder_sent_at: r.reminder_sent_at,
                    current_stage: r.current_stage,
                  });
                // The "N days late" pill already states the day count for
                // stages 1–3; only "Final notice" (stage ≥ 4) hides it, so
                // it's appended to the due line there instead of on its own
                // separate line.
                const dueLine =
                  r.payment_status === "paid" || !r.reminders_enabled
                    ? null
                    : moveOutDue
                      ? `${t("admin.remindersTab.duePrefix", "Due")} ${isPast ? formatDateLong(moveOutDue) : formatDateShort(moveOutDue, language)}${
                          r.current_stage >= 4
                            ? ` · ${r.days_overdue} ${t("admin.remindersTab.daysLate", "days late")}`
                            : ""
                        }`
                      : null;
                return (
                  <li key={r.row_key}>
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => onOpenTenant(r.row_key)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ")
                          onOpenTenant(r.row_key);
                      }}
                      className="w-full text-left grid grid-cols-1 lg:grid-cols-[minmax(0,1.7fr)_minmax(0,1.2fr)_minmax(0,1.9fr)_minmax(0,0.85fr)_minmax(0,1.05fr)] gap-3 px-5 py-4 hover:bg-muted/40 transition-colors"
                    >
                      <Link
                        to={`/admin?tab=tenants&bookingId=${r.booking_id}`}
                        onClick={(e) => e.stopPropagation()}
                        className="flex items-center gap-3 min-w-0 group/name"
                      >
                        <div className="h-9 w-9 shrink-0 rounded-full bg-rose-50 text-rose-700 flex items-center justify-center text-xs font-semibold">
                          {(r.tenant_name || r.tenant_email || "?")
                            .split(" ")
                            .filter(Boolean)
                            .slice(0, 2)
                            .map((p) => p[0]?.toUpperCase())
                            .join("") || "?"}
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-foreground truncate group-hover/name:text-emerald-700 group-hover/name:underline transition-colors">
                            {r.tenant_name ||
                              `${t("owner.reminders.bookingHashPrefix", "Booking")} #${r.booking_id}`}
                          </div>
                          <div className="text-xs text-muted-foreground truncate">
                            {r.tenant_email ??
                              `${t("owner.reminders.bookingHashPrefix", "Booking")} #${r.booking_id}`}
                          </div>
                        </div>
                      </Link>

                      <div className="min-w-0">
                        <div className="text-sm text-foreground break-words">
                          {r.room_title || "—"}
                        </div>
                        {r.property_title ? (
                          <div className="text-xs text-muted-foreground break-words">
                            {r.property_title}
                            {r.property_city ? `, ${r.property_city}` : ""}
                          </div>
                        ) : null}
                      </div>

                      <div className="flex flex-col items-start justify-center gap-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <StatusPill row={r} />
                          {dueLine ? (
                            <span className="text-xs text-muted-foreground">
                              {dueLine}
                            </span>
                          ) : null}
                        </div>
                        {sentLine || nextLine ? (
                          <div className="text-[11px] text-muted-foreground/80 leading-snug">
                            {sentLine}
                            {sentLine && nextLine ? <br /> : null}
                            {/* A scheduled reminder that never went out is an
                                automation failure, not a status update: amber
                                so it reads as "something is wrong here" and
                                not as one more line of grey metadata. */}
                            {nextLine && nextMissed ? (
                              <span className="inline-flex items-center gap-1 font-medium text-amber-700">
                                <TriangleAlert className="h-3 w-3 shrink-0" />
                                {nextLine}
                              </span>
                            ) : (
                              nextLine
                            )}
                          </div>
                        ) : null}
                      </div>

                      <div>
                        <div
                          className={cn(
                            "text-sm font-semibold tabular-nums",
                            r.payment_status === "paid" || !r.reminders_enabled
                              ? "text-muted-foreground/60"
                              : r.current_stage >= 3
                                ? "text-rose-700"
                                : "text-foreground"
                          )}
                        >
                          {r.payment_status === "paid" || !r.reminders_enabled
                            ? "—"
                            : formatEur(totalOwed)}
                        </div>
                        {r.payment_status !== "paid" && r.reminders_enabled ? (
                          <div className="text-[11px] text-muted-foreground">
                            {formatEur(r.monthly_rent)}{" "}
                            {t("owner.reminders.rentWord", "rent")}
                            {r.accumulated_fees_eur > 0 ? (
                              <>
                                {" "}
                                · {formatEur(r.accumulated_fees_eur)}{" "}
                                {t("owner.reminders.feesWord", "fees")}
                              </>
                            ) : (
                              <>
                                {" "}
                                ·{" "}
                                {t("owner.reminders.noFeesYet", "No fees yet")}
                              </>
                            )}
                          </div>
                        ) : null}
                        {/* COL-417: the headline above is what is still owed. This says why it
                            is lower than the month's rent — without it a part-paid row just
                            looks like the rent was edited. */}
                        {r.rent_received_eur > 0 ? (
                          <div className="text-[11px] font-semibold tabular-nums text-blue-700">
                            {formatEur(r.rent_received_eur)}{" "}
                            {t("owner.reminders.ofBilled", "of")}{" "}
                            {formatEur(r.rent_billed_eur)}{" "}
                            {t("owner.reminders.receivedWord", "received")}
                          </div>
                        ) : null}
                      </div>

                      <div className="flex flex-col items-center justify-center gap-1.5">
                        {!r.reminders_enabled ? (
                          <span className="text-xs text-muted-foreground">
                            {t("owner.reminders.notTracked", "Not tracked")}
                          </span>
                        ) : r.payment_status === "paid" ? (
                          <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">
                            {t("owner.reminders.upToDate", "Up to date")}
                          </Badge>
                        ) : readOnly ? null : (
                          <Button
                            type="button"
                            size="sm"
                            variant={
                              r.current_stage === 0 ? "outline" : "default"
                            }
                            className={cn(
                              "h-7 rounded-lg px-2.5 text-xs shrink-0 whitespace-nowrap",
                              r.current_stage === 0
                                ? ""
                                : "bg-emerald-700 hover:bg-emerald-800 text-white"
                            )}
                            disabled={r.current_stage === 0}
                            onClick={(e) => {
                              e.stopPropagation();
                              onDraft(r);
                            }}
                          >
                            {t(
                              "admin.remindersTab.sendNextReminder",
                              "Send next reminder"
                            )}
                          </Button>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>
      ) : null}

      {/* Pagination */}
      {filteredSorted.length > PAGE_SIZE ? (
        <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="text-xs text-muted-foreground">
            {t("owner.reminders.showingPrefix", "Showing")}{" "}
            <span className="font-medium text-foreground">
              {(safePage - 1) * PAGE_SIZE + 1}–
              {Math.min(safePage * PAGE_SIZE, filteredSorted.length)}
            </span>{" "}
            {t("owner.reminders.ofWord", "of")}{" "}
            <span className="font-medium text-foreground">
              {filteredSorted.length}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="rounded-xl h-8"
              disabled={safePage <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              {t("owner.reminders.prev", "Prev")}
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="rounded-xl h-8"
              disabled={safePage >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              {t("owner.reminders.next", "Next")}
            </Button>
          </div>
        </div>
      ) : null}
    </>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Detail view (single tenant)
// ───────────────────────────────────────────────────────────────────────────

function DetailView({
  row,
  activity,
  readOnly = false,
  onBack,
  onDraft,
  onMarkPaid,
  onOpenProfile,
}: {
  row: TenantRow;
  activity: ActivityRow[];
  /** Owner-portal viewers see dunning state read-only — no send/mark-paid. */
  readOnly?: boolean;
  onBack: () => void;
  onDraft: () => void;
  onMarkPaid: () => void;
  onOpenProfile: (bookingId: number) => void;
}) {
  const { t, language } = useLanguage();
  const today = new Date();
  const periodDue = rowPeriodDue(row, today);
  const totalOwed = row.monthly_rent + row.accumulated_fees_eur;
  const isPaid = row.payment_status === "paid";
  const stage = row.current_stage;

  return (
    <Card className="overflow-hidden">
      {/* Back link */}
      <div className="px-5 sm:px-6 pt-4">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="-ml-2 text-muted-foreground hover:text-foreground"
          onClick={onBack}
        >
          <ArrowLeft className="h-4 w-4 mr-1.5" />
          {t("admin.remindersTab.allTenants", "All tenants")}
        </Button>
      </div>

      {/* Header */}
      <div className="p-5 sm:p-6 pt-3 flex items-start gap-4 flex-wrap">
        <button
          type="button"
          onClick={() => onOpenProfile(row.booking_id)}
          className="h-14 w-14 shrink-0 rounded-full bg-emerald-700 text-white flex items-center justify-center text-lg font-bold cursor-pointer hover:opacity-80 transition-opacity"
        >
          {(row.tenant_name || row.tenant_email || "?")
            .split(" ")
            .filter(Boolean)
            .slice(0, 2)
            .map((p) => p[0]?.toUpperCase())
            .join("") || "?"}
        </button>
        <div className="flex-1 min-w-[260px]">
          <div className="flex items-center gap-2 flex-wrap">
            <h3
              className="text-2xl font-bold text-foreground tracking-tight cursor-pointer hover:underline"
              onClick={() => onOpenProfile(row.booking_id)}
            >
              {row.tenant_name ||
                `${t("owner.reminders.bookingHashPrefix", "Booking")} #${row.booking_id}`}
            </h3>
            <StatusPill row={row} />
          </div>
          <div className="text-sm text-muted-foreground mt-1">
            {row.room_title
              ? `${t("admin.remindersTab.roomPrefix", "Room")} ${row.room_title}`
              : `${t("owner.reminders.bookingHashPrefix", "Booking")} #${row.booking_id}`}
            {row.property_title ? ` · ${row.property_title}` : ""}
            {row.property_city ? `, ${row.property_city}` : ""}
            {row.tenant_email ? <> · {row.tenant_email}</> : null}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {!isPaid && !readOnly ? (
            <Button
              type="button"
              size="sm"
              className="rounded-xl bg-emerald-700 hover:bg-emerald-800 text-white"
              disabled={stage === 0}
              onClick={onDraft}
            >
              <Send className="h-3.5 w-3.5 mr-1.5" />
              {t("admin.remindersTab.sendNextReminder", "Send next reminder")}
            </Button>
          ) : null}
        </div>
      </div>

      {/* Alert banner */}
      {!isPaid && stage > 0 ? (
        <div className="px-5 sm:px-6 pb-5 sm:pb-6">
          <AlertBanner row={row} />
        </div>
      ) : null}

      {/* Two-column body */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-5 px-5 sm:px-6 pb-5 sm:pb-6">
        <div className="space-y-4">
          <div className="rounded-2xl border border-border/60 bg-background p-4">
            <div className="mb-3">
              <div className="text-sm font-semibold text-foreground">
                {t("owner.reminders.reminderStage", "Reminder stage")}
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">
                {t(
                  "owner.reminders.manualEscalationHint",
                  "Manual escalation — admin reviews and dispatches each stage."
                )}
              </div>
            </div>
            <StageTracker row={row} />
          </div>
          <div className="rounded-2xl border border-border/60 bg-background p-4">
            <div className="mb-3">
              <div className="text-sm font-semibold text-foreground">
                {t("owner.reminders.activity", "Activity")}
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">
                {t(
                  "owner.reminders.activityHint",
                  "Payments received, reminders sent, status changes."
                )}
              </div>
            </div>
            <ActivityTimeline
              rows={activity}
              currentStage={stage}
              feePerStage={row.fee_per_stage_eur}
            />
          </div>
        </div>

        <div>
          <div className="rounded-2xl border border-border/60 bg-background p-4 sm:p-5 sticky top-4">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-semibold text-foreground">
                {isPaid
                  ? t("admin.remindersTab.lastPayment", "Last payment")
                  : t("owner.reminders.outstanding", "Outstanding")}
              </div>
              {!isPaid && stage > 0 ? (
                <Badge className="bg-orange-50 text-orange-800 border-orange-200">
                  {`${t("owner.reminders.stagePrefix", "Stage")} ${stage} ${t("owner.reminders.activeSuffix", "active")}`}
                </Badge>
              ) : null}
            </div>
            {isPaid ? (
              <>
                <div className="text-3xl font-bold text-foreground tabular-nums leading-tight mb-2">
                  {formatEur(row.monthly_rent)}
                </div>
                {row.last_payment_received_at ? (
                  <div className="text-xs text-muted-foreground">
                    {t("admin.remindersTab.receivedPrefix", "Received")}{" "}
                    {formatDateLong(new Date(row.last_payment_received_at))}
                  </div>
                ) : null}
              </>
            ) : (
              <>
                <div className="text-3xl font-bold text-foreground tabular-nums leading-tight mb-3">
                  {formatEur(totalOwed)}
                </div>
                <div className="border-t border-border/60 pt-3 space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">
                      {t("owner.reminders.rentWord", "Rent")} ·{" "}
                      {language === "de"
                        ? monthLabelDe(periodDue)
                        : monthLabelEn(periodDue)}
                    </span>
                    <span className="font-medium tabular-nums">
                      {formatEur(row.monthly_rent)}
                    </span>
                  </div>
                  {row.accumulated_fees_eur > 0 ? (
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">
                        {t("owner.reminders.mahngebuehren", "Mahngebühren")}
                      </span>
                      <span className="font-medium tabular-nums text-rose-700">
                        +{formatEur(row.accumulated_fees_eur)}
                      </span>
                    </div>
                  ) : null}
                  {stage > row.reminder_stage && stage > 1 ? (
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-muted-foreground">
                        {`${t("owner.reminders.stagePrefix", "Stage")} ${stage} ${t("owner.reminders.feeOnSendSuffix", "fee (on send)")}`}
                      </span>
                      <span className="font-medium tabular-nums text-rose-700">
                        +{formatEur(row.fee_per_stage_eur)}
                      </span>
                    </div>
                  ) : null}
                  <div className="flex items-center justify-between border-t border-border/60 pt-2">
                    <span className="font-semibold">
                      {t("owner.reminders.totalOwed", "Total owed")}
                    </span>
                    <span className="font-bold tabular-nums">
                      {formatEur(totalOwed)}
                    </span>
                  </div>
                </div>
                {!readOnly ? (
                  <div className="mt-4 space-y-2">
                    <Button
                      type="button"
                      className="w-full rounded-xl bg-emerald-700 hover:bg-emerald-800 text-white"
                      disabled={stage === 0}
                      onClick={onDraft}
                    >
                      <Send className="h-4 w-4 mr-1.5" />
                      {stage === 0
                        ? t(
                            "owner.reminders.noReminderDueYet",
                            "No reminder due yet"
                          )
                        : t(
                            "admin.remindersTab.sendNextReminder",
                            "Send next reminder"
                          )}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full rounded-xl"
                      onClick={onMarkPaid}
                    >
                      <CheckCircle2 className="h-4 w-4 mr-1.5" />
                      {t("owner.reminders.markAsPaid", "Mark as paid")}
                    </Button>
                  </div>
                ) : null}
                {stage > 0 && stage < 4 ? (
                  <div className="mt-3 rounded-lg bg-muted/40 px-3 py-2 text-[11px] text-muted-foreground leading-relaxed">
                    <span className="font-semibold text-foreground">
                      {t("owner.reminders.nextPrefix", "Next:")}
                    </span>{" "}
                    {stage === 1
                      ? `${t("owner.reminders.nextStage2", "1. Mahnung at Day 5+")} (+${feeLabel(row.fee_per_stage_eur)}).`
                      : stage === 2
                        ? `${t("owner.reminders.nextStage3", "2. Mahnung at Day 7+")} (+${feeLabel(row.fee_per_stage_eur)}).`
                        : t(
                            "owner.reminders.nextStage4",
                            "Inkasso referral at Day 9+. Stage 4 hands the case to the collection agency."
                          )}
                  </div>
                ) : null}
                {stage === 4 ? (
                  <div className="mt-3 rounded-lg bg-rose-50 border border-rose-200 px-3 py-2 text-[11px] text-rose-900 leading-relaxed">
                    <span className="font-semibold">
                      {t("owner.reminders.inkassoActive", "Inkasso active.")}
                    </span>{" "}
                    {`${t("owner.reminders.inkassoRepeatsPrefix", "Stage 4 repeats every 2 days, adding")} ${feeLabel(row.fee_per_stage_eur)} ${t("owner.reminders.inkassoRepeatsSuffix", "each send. Additional inkasso & court costs apply outside this system.")}`}
                  </div>
                ) : null}
              </>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}

function AlertBanner({ row }: { row: TenantRow }) {
  const { t } = useLanguage();
  const stage = row.current_stage as ReminderStage;
  const next =
    stage === 1
      ? t(
          "owner.reminders.alert.next1",
          "1. Mahnung becomes available at Day 5+"
        )
      : stage === 2
        ? t(
            "owner.reminders.alert.next2",
            "2. Mahnung becomes available at Day 7+"
          )
        : stage === 3
          ? t(
              "admin.remindersTab.alert.next3",
              "Stage 4 (Inkasso referral) becomes available at Day 9+"
            )
          : t(
              "owner.reminders.alert.next4",
              "Stage 4 repeats every 2 days while unpaid"
            );
  const headline =
    stage === 1
      ? `${row.days_overdue} ${t("admin.remindersTab.alert.overdueSuffix", "days overdue")} · ${t("admin.remindersTab.alert.friendlyDueNow", "Friendly reminder due now")}`
      : stage === 2
        ? `${row.days_overdue} ${t("admin.remindersTab.alert.overdueSuffix", "days overdue")} · ${t("admin.remindersTab.alert.firstMahnungDueNow", "1st Mahnung due now")}`
        : stage === 3
          ? `${row.days_overdue} ${t("admin.remindersTab.alert.overdueSuffix", "days overdue")} · ${t("admin.remindersTab.alert.secondMahnungDueNow", "2nd Mahnung due now")}`
          : `${row.days_overdue} ${t("admin.remindersTab.alert.overdueSuffix", "days overdue")} · ${t("admin.remindersTab.alert.inkassoDueNow", "Inkasso referral due now")}`;
  const tone =
    stage === 1
      ? "border-amber-200 bg-amber-50 text-amber-900"
      : stage === 2 || stage === 3
        ? "border-orange-200 bg-orange-50 text-orange-900"
        : "border-rose-200 bg-rose-50 text-rose-900";
  return (
    <div className={cn("rounded-2xl border p-4 flex items-start gap-3", tone)}>
      <AlertCircle className="h-5 w-5 shrink-0 mt-0.5" />
      <div>
        <div className="text-sm font-semibold">{headline}</div>
        <div className="text-xs opacity-90 mt-0.5">{next}.</div>
      </div>
    </div>
  );
}

function StageTracker({ row }: { row: TenantRow }) {
  const { t, language } = useLanguage();
  const labels = [
    t("owner.reminders.stage.friendly", "Friendly"),
    "1. Mahnung",
    "2. Mahnung",
    "Inkasso",
  ] as const;
  const sentUpTo = row.reminder_stage;
  const currentStage = row.current_stage;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
      {labels.map((label, i) => {
        const stage = (i + 1) as 1 | 2 | 3 | 4;
        const isSent = stage <= sentUpTo;
        const isCurrent = stage === currentStage && !isSent;
        const sentDateLabel =
          isSent && row.reminder_sent_at && stage === sentUpTo
            ? `${t("owner.reminders.stage.sent", "Sent")} · ${formatDateShort(new Date(row.reminder_sent_at), language)}`
            : isSent
              ? t("owner.reminders.stage.sent", "Sent")
              : null;
        return (
          <div
            key={label}
            className={cn(
              "rounded-xl border p-3 text-xs",
              isSent
                ? "border-emerald-200 bg-emerald-50 text-emerald-900"
                : isCurrent
                  ? "border-orange-300 bg-orange-50 text-orange-900 ring-1 ring-orange-300"
                  : "border-border/60 bg-muted/30 text-muted-foreground"
            )}
          >
            <div className="flex items-center gap-1.5 mb-1">
              <span
                className={cn(
                  "h-5 w-5 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0",
                  isSent
                    ? "bg-emerald-600 text-white"
                    : isCurrent
                      ? "bg-orange-600 text-white"
                      : "bg-background border border-border text-muted-foreground"
                )}
              >
                {isSent ? <CheckCircle2 className="h-3 w-3" /> : stage}
              </span>
              <span className="font-semibold truncate">{label}</span>
            </div>
            <div className="text-[11px] leading-tight">
              {sentDateLabel
                ? sentDateLabel
                : isCurrent
                  ? `${t("owner.reminders.stage.dueNow", "Due now")}${stage > 1 ? ` · +${feeLabel(row.fee_per_stage_eur)}` : ""}`
                  : stage === 1
                    ? t("owner.reminders.stage.atDay3", "At Day 3+")
                    : stage === 2
                      ? `${t("owner.reminders.stage.atDay5", "At Day 5+")} · +${feeLabel(row.fee_per_stage_eur)}`
                      : stage === 3
                        ? `${t("owner.reminders.stage.atDay7", "At Day 7+")} · +${feeLabel(row.fee_per_stage_eur)}`
                        : `${t("owner.reminders.stage.atDay9", "At Day 9+")} · +${feeLabel(row.fee_per_stage_eur)}`}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ActivityTimeline({
  rows,
  currentStage,
  feePerStage,
}: {
  rows: ActivityRow[];
  currentStage: number;
  feePerStage: number;
}) {
  const { t, language } = useLanguage();
  const items: Array<{
    key: string;
    title: string;
    sub: string;
    color: "emerald" | "orange" | "amber" | "rose" | "slate";
    pending?: boolean;
  }> = [];

  if (currentStage > 0 && rows.length === 0) {
    items.push({
      key: "pending-top",
      title: `${t("owner.reminders.stagePrefix", "Stage")} ${currentStage} ${t("owner.reminders.activityLog.draftPendingSuffix", "reminder — draft pending")}`,
      sub: t(
        "owner.reminders.activityLog.awaitingAdminSend",
        "Awaiting admin send"
      ),
      color: "orange",
      pending: true,
    });
  }

  for (const r of rows) {
    const ts = new Date(r.created_at).toLocaleString(
      language === "de" ? "de-DE" : "en-GB",
      {
        day: "numeric",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      }
    );
    if (r.event_type === "reminder_sent") {
      const stageLabel =
        r.stage === 1
          ? t(
              "owner.reminders.activityLog.friendlyReminder",
              "Friendly reminder"
            )
          : r.stage === 2
            ? "1. Mahnung"
            : r.stage === 3
              ? "2. Mahnung"
              : t(
                  "owner.reminders.activityLog.inkassoReferral",
                  "Inkasso referral"
                );
      items.push({
        key: r.id,
        title: `${stageLabel} ${t("owner.reminders.activityLog.sentWithLangSuffix", "sent")} (${(r.email_lang ?? "de").toUpperCase()})`,
        sub: `${ts}${r.stage && r.stage > 1 ? ` · +${feeLabel(feePerStage)} Mahngebühr` : ""}`,
        color: r.stage === 4 ? "rose" : r.stage === 1 ? "amber" : "orange",
      });
    } else if (r.event_type === "marked_paid") {
      items.push({
        key: r.id,
        title: `${t("owner.reminders.activityLog.markedAsPaidPrefix", "Marked as paid")} · ${formatEur(Number(r.amount_eur ?? 0))}`,
        sub: ts,
        color: "emerald",
      });
    } else if (r.event_type === "payment_received") {
      items.push({
        key: r.id,
        title: `${t("owner.reminders.activityLog.paymentReceivedPrefix", "Payment received")} · ${formatEur(Number(r.amount_eur ?? 0))}`,
        sub: ts,
        color: "emerald",
      });
    } else if (r.event_type === "inkasso_referred") {
      items.push({
        key: r.id,
        title: t(
          "owner.reminders.activityLog.caseReferredToInkasso",
          "Case referred to Inkasso"
        ),
        sub: ts,
        color: "rose",
      });
    }
  }

  if (items.length === 0) {
    return (
      <div className="text-sm text-muted-foreground py-3">
        {t(
          "owner.reminders.activityLog.noActivityYet",
          "No activity yet. Reminder sends and payments will appear here."
        )}
      </div>
    );
  }

  const dotColor: Record<string, string> = {
    emerald: "bg-emerald-500",
    orange: "bg-orange-500",
    amber: "bg-amber-500",
    rose: "bg-rose-500",
    slate: "bg-slate-400",
  };

  return (
    <ol className="relative ml-1 border-l border-border/60 pl-5 space-y-4">
      {items.map((it) => (
        <li key={it.key} className="relative">
          <span
            className={cn(
              "absolute -left-[27px] top-1 h-3 w-3 rounded-full ring-4 ring-background",
              dotColor[it.color],
              it.pending && "animate-pulse"
            )}
          />
          <div className="text-sm font-medium text-foreground">{it.title}</div>
          <div className="text-xs text-muted-foreground">{it.sub}</div>
        </li>
      ))}
    </ol>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Email draft modal
// ───────────────────────────────────────────────────────────────────────────

function EmailDraftDialog({
  row,
  preview,
  subject,
  setSubject,
  html,
  setHtml,
  text,
  setText,
  lang,
  setLang,
  onClose,
  onSaveDraft,
  onSend,
  sending,
}: {
  row: TenantRow | null;
  preview: {
    subject: string;
    html: string;
    text: string;
    stage: ReminderStage;
    feeCount: number;
  } | null;
  subject: string;
  setSubject: (s: string) => void;
  html: string;
  setHtml: (s: string) => void;
  text: string;
  setText: (s: string) => void;
  lang: ReminderLang;
  setLang: (l: ReminderLang) => void;
  onClose: () => void;
  onSaveDraft: () => void;
  onSend: () => void;
  sending: boolean;
}) {
  const { t } = useLanguage();
  const open = row != null;
  const stageLabel = preview
    ? `${t("owner.reminders.stagePrefix", "Stage")} ${preview.stage}`
    : t("owner.reminders.stagePrefix", "Stage");
  const previewDoc = (html || preview?.html || "").replace(
    /<script[\s\S]*?<\/script>/gi,
    ""
  );
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[820px] max-h-[92vh] p-0 overflow-hidden flex flex-col rounded-2xl">
        <DialogHeader className="p-5 sm:p-6 border-b border-border/60 shrink-0">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <DialogTitle className="text-lg font-semibold flex items-center gap-2">
                <span className="h-9 w-9 rounded-xl bg-orange-100 text-orange-700 flex items-center justify-center shrink-0">
                  <Send className="h-4 w-4" />
                </span>
                {t("owner.reminders.draft.draftPrefix", "Draft")} {stageLabel} —{" "}
                {row?.tenant_name ??
                  `${t("owner.reminders.bookingHashPrefix", "Booking")} #${row?.booking_id}`}
              </DialogTitle>
              <DialogDescription className="text-sm text-muted-foreground mt-0.5">
                {`${t("owner.reminders.stagePrefix", "Stage")} ${preview?.stage ?? "—"}`}{" "}
                ·{" "}
                {t(
                  "owner.reminders.draft.reviewingBeforeSend",
                  "Reviewing before send"
                )}{" "}
                · {t("owner.reminders.draft.fromLabel", "From")}{" "}
                <code className="px-1 rounded bg-muted text-[11px]">
                  rechnungen@coliviq.de
                </code>
              </DialogDescription>
            </div>
            <div className="inline-flex rounded-full border border-border/60 bg-muted/30 p-1 text-xs">
              <button
                type="button"
                onClick={() => setLang("de")}
                className={cn(
                  "rounded-full px-3 py-1 font-medium",
                  lang === "de"
                    ? "bg-background shadow-sm text-foreground"
                    : "text-muted-foreground"
                )}
              >
                🇩🇪 DE
              </button>
              <button
                type="button"
                onClick={() => setLang("en")}
                className={cn(
                  "rounded-full px-3 py-1 font-medium",
                  lang === "en"
                    ? "bg-background shadow-sm text-foreground"
                    : "text-muted-foreground"
                )}
              >
                🇬🇧 EN
              </button>
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto p-5 sm:p-6 space-y-4">
          <div className="rounded-2xl border border-border/60 bg-muted/30 overflow-hidden">
            <div className="grid grid-cols-[80px_1fr] divide-y divide-border/60">
              <div className="px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">
                {t("admin.remindersTab.emailDialog.from", "From")}
              </div>
              <div className="px-4 py-3 text-sm">
                coliviq Rechnungen{" "}
                <span className="text-muted-foreground">
                  &lt;rechnungen@coliviq.de&gt;
                </span>
              </div>
              <div className="px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground font-medium border-t border-border/60">
                {t("admin.remindersTab.emailDialog.to", "To")}
              </div>
              <div className="px-4 py-3 text-sm border-t border-border/60">
                {row?.tenant_name ?? "—"}{" "}
                {row?.tenant_email ? (
                  <span className="text-muted-foreground">
                    &lt;{row.tenant_email}&gt;
                  </span>
                ) : null}
              </div>
              <div className="px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground font-medium border-t border-border/60">
                <Label htmlFor="reminder-subject">
                  {t("admin.remindersTab.emailDialog.subject", "Subject")}
                </Label>
              </div>
              <div className="px-4 py-2 border-t border-border/60">
                <Input
                  id="reminder-subject"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="h-9 rounded-lg bg-background border-border/60"
                />
              </div>
            </div>
          </div>

          {preview ? (
            <div className="rounded-2xl border border-border/60 overflow-hidden bg-white">
              <iframe
                title={t(
                  "admin.remindersTab.emailPreviewTitle",
                  "Email preview"
                )}
                className="w-full h-[720px] bg-white"
                sandbox="allow-same-origin"
                srcDoc={previewDoc}
              />
            </div>
          ) : null}

          {!row?.tenant_email ? (
            <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 flex items-start gap-2">
              <AlertCircle className="h-4 w-4 text-amber-700 mt-0.5 shrink-0" />
              <div className="text-xs text-amber-900">
                {t(
                  "admin.remindersTab.noTenantEmailWarning",
                  "This tenant has no email address on file. Add one to their profile before sending."
                )}
              </div>
            </div>
          ) : null}
          <div className="text-[11px] text-muted-foreground text-center">
            {t(
              "admin.remindersTab.emailArchivedNotice",
              "A copy of this email will be archived and an activity log entry written."
            )}
          </div>
        </div>

        <DialogFooter className="p-4 sm:p-5 border-t border-border/60 shrink-0 bg-muted/10">
          <Button type="button" variant="ghost" onClick={onClose}>
            {t("admin.action.cancel", "Cancel")}
          </Button>
          <Button
            type="button"
            variant="outline"
            className="rounded-xl"
            disabled={sending || !row?.tenant_email || !preview}
            onClick={onSaveDraft}
          >
            {t("admin.remindersTab.emailDialog.saveDraft", "Save draft")}
          </Button>
          <Button
            type="button"
            className="rounded-xl bg-emerald-700 hover:bg-emerald-800 text-white"
            disabled={sending || !row?.tenant_email || !preview}
            onClick={onSend}
          >
            {sending ? (
              <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
            ) : (
              <Send className="h-4 w-4 mr-1.5" />
            )}
            {t("admin.remindersTab.emailDialog.sendEmail", "Send email")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AdminEmailDialog({
  row,
  subject,
  setSubject,
  html,
  setHtml,
  text,
  setText,
  onClose,
  onSend,
  sending,
}: {
  row: TenantRow | null;
  subject: string;
  setSubject: (s: string) => void;
  html: string;
  setHtml: (s: string) => void;
  text: string;
  setText: (s: string) => void;
  onClose: () => void;
  onSend: () => void;
  sending: boolean;
}) {
  const { t } = useLanguage();
  const open = row != null;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[820px] max-h-[92vh] p-0 overflow-hidden flex flex-col rounded-2xl">
        <DialogHeader className="p-5 sm:p-6 border-b border-border/60 shrink-0">
          <DialogTitle className="text-lg font-semibold flex items-center gap-2">
            <span className="h-9 w-9 rounded-xl bg-slate-100 text-slate-700 flex items-center justify-center shrink-0">
              <Mail className="h-4 w-4" />
            </span>
            {t(
              "admin.remindersTab.emailDialog.emailTenantPrefix",
              "Email tenant"
            )}{" "}
            —{" "}
            {row?.tenant_name ??
              `${t("owner.reminders.bookingHashPrefix", "Booking")} #${row?.booking_id}`}
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground mt-0.5">
            {t("admin.remindersTab.emailDialog.from", "From")}{" "}
            <code className="px-1 rounded bg-muted text-[11px]">
              rechnungen@coliviq.de
            </code>{" "}
            · {t("admin.remindersTab.emailDialog.to", "To")}{" "}
            <code className="px-1 rounded bg-muted text-[11px]">
              {row?.tenant_email ?? "—"}
            </code>
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto p-5 sm:p-6 space-y-4">
          <div className="grid gap-2">
            <Label htmlFor="admin-email-subject">
              {t("admin.remindersTab.emailDialog.subject", "Subject")}
            </Label>
            <Input
              id="admin-email-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="h-9 rounded-lg bg-background border-border/60"
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="admin-email-text">
              {t(
                "admin.remindersTab.emailDialog.plainTextOptional",
                "Plain text (optional)"
              )}
            </Label>
            <textarea
              id="admin-email-text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              className="w-full min-h-[120px] rounded-xl border border-border/60 bg-background p-3 text-xs font-mono"
            />
          </div>

          {html.trim() ? (
            <div className="rounded-2xl border border-border/60 overflow-hidden bg-white">
              <iframe
                title={t(
                  "admin.remindersTab.emailPreviewTitle",
                  "Email preview"
                )}
                className="w-full h-[520px] bg-white"
                sandbox="allow-same-origin"
                srcDoc={html}
              />
            </div>
          ) : null}
        </div>

        <DialogFooter className="p-4 sm:p-5 border-t border-border/60 shrink-0 bg-muted/10">
          <Button type="button" variant="ghost" onClick={onClose}>
            {t("admin.action.cancel", "Cancel")}
          </Button>
          <Button
            type="button"
            className="rounded-xl"
            disabled={
              !row?.tenant_email ||
              !subject.trim() ||
              (!html.trim() && !text.trim()) ||
              sending
            }
            onClick={onSend}
          >
            {sending
              ? t("admin.remindersTab.emailDialog.sending", "Sending…")
              : t("admin.remindersTab.emailDialog.sendEmail", "Send email")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Mark paid dialog
// ───────────────────────────────────────────────────────────────────────────

function MarkPaidDialog({
  row,
  onClose,
  onConfirm,
  confirming,
}: {
  row: TenantRow | null;
  onClose: () => void;
  onConfirm: () => void;
  confirming: boolean;
}) {
  const { t, language } = useLanguage();
  const open = row != null;
  const totalOwed = (row?.monthly_rent ?? 0) + (row?.accumulated_fees_eur ?? 0);
  const periodLabel =
    row && (row.period_due_date || row.rent_due_date != null)
      ? language === "de"
        ? monthLabelDe(rowPeriodDue(row))
        : monthLabelEn(rowPeriodDue(row))
      : "";
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[480px] rounded-2xl">
        <DialogHeader>
          <DialogTitle>
            {t(
              "admin.remindersTab.markPaidDialog.title",
              "Confirm payment received"
            )}
          </DialogTitle>
          <DialogDescription>
            {row ? (
              <>
                {t(
                  "admin.remindersTab.markPaidDialog.confirmPaymentOf",
                  "Confirm payment of"
                )}{" "}
                <strong className="text-foreground">
                  {formatEur(totalOwed)}
                </strong>{" "}
                {t(
                  "admin.remindersTab.markPaidDialog.receivedFrom",
                  "received from"
                )}{" "}
                <strong className="text-foreground">
                  {row.tenant_name ||
                    row.tenant_email ||
                    `${t("owner.reminders.bookingHashPrefix", "Booking")} #${row.booking_id}`}
                </strong>
                {periodLabel ? (
                  <>
                    {" "}
                    {t(
                      "admin.remindersTab.markPaidDialog.forPeriod",
                      "for"
                    )}{" "}
                    {periodLabel}
                  </>
                ) : (
                  ""
                )}
                ?
              </>
            ) : null}
          </DialogDescription>
        </DialogHeader>
        {row && row.accumulated_fees_eur > 0 ? (
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 space-y-1">
            <div>
              <strong>
                {t(
                  "admin.remindersTab.markPaidDialog.includesFees",
                  "Includes Mahngebühren:"
                )}
              </strong>{" "}
              {formatEur(row.accumulated_fees_eur)}
            </div>
            <div>
              {t(
                "admin.remindersTab.markPaidDialog.feesConfirmNote",
                "Confirm with the tenant whether the fees were collected. Fees are tracked separately and reset to €0 upon confirmation."
              )}
            </div>
          </div>
        ) : null}
        {row?.inkasso_referred_at ? (
          <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-xs text-rose-900">
            <strong>
              {t(
                "admin.remindersTab.markPaidDialog.inkassoRetained",
                "Inkasso reference retained."
              )}
            </strong>{" "}
            {t(
              "admin.remindersTab.markPaidDialog.casePassedOn",
              "Case was passed to the collector on"
            )}{" "}
            {formatDateNumeric(row.inkasso_referred_at, language)}.{" "}
            {t(
              "admin.remindersTab.markPaidDialog.timestampAudit",
              "That timestamp stays on file for audit."
            )}
          </div>
        ) : null}
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose}>
            {t("admin.action.cancel", "Cancel")}
          </Button>
          <Button
            type="button"
            className="rounded-xl bg-emerald-700 hover:bg-emerald-800 text-white"
            disabled={confirming}
            onClick={onConfirm}
          >
            {confirming ? (
              <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
            ) : (
              <CheckCircle2 className="h-4 w-4 mr-1.5" />
            )}
            {t(
              "admin.remindersTab.markPaidDialog.confirmPayment",
              "Confirm payment"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
